import React, { useState, Fragment, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Box, Card, CardContent, Grid, Typography, FormControlLabel, Switch,
    Tooltip, TextField, Stack, Chip, Table, TableBody, TableCell,
    TableHead, TableRow, IconButton, Button, CircularProgress,
    MenuItem, Tabs, Tab, Checkbox, Dialog, DialogTitle, DialogContent,
    DialogActions, Paper, Avatar, Divider, Alert, Snackbar,
    InputAdornment, Select, FormControl, InputLabel,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Link, Redirect, useLocation } from 'react-router-dom';
import EditIcon          from '@mui/icons-material/Edit';
import SaveIcon          from '@mui/icons-material/Save';
import PersonAddAltIcon  from '@mui/icons-material/PersonAddAlt';
import BusinessIcon      from '@mui/icons-material/Business';
import EmailIcon         from '@mui/icons-material/Email';
import CameraAltIcon     from '@mui/icons-material/CameraAlt';
import AddIcon           from '@mui/icons-material/Add';
import DeleteIcon        from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import LockResetIcon     from '@mui/icons-material/LockReset';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FolderOpenIcon    from '@mui/icons-material/FolderOpen';
import ArticleIcon      from '@mui/icons-material/Article';
import { TEMPLATE_KINDS } from '../templates/templateKinds';
import VerifiedUserIcon  from '@mui/icons-material/VerifiedUser';
import SecurityIcon      from '@mui/icons-material/Security';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PercentIcon       from '@mui/icons-material/Percent';
import InfoOutlinedIcon  from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon  from '@mui/icons-material/ArrowForward';
import StorefrontIcon    from '@mui/icons-material/Storefront';
import ApartmentIcon     from '@mui/icons-material/Apartment';
import LocationOnIcon    from '@mui/icons-material/LocationOn';
import PhoneIcon         from '@mui/icons-material/Phone';
import BadgeIcon              from '@mui/icons-material/Badge';
import QrCodeScannerIcon      from '@mui/icons-material/QrCodeScanner';
import OpenInNewIcon          from '@mui/icons-material/OpenInNew';
import ContentCopyIcon        from '@mui/icons-material/ContentCopy';
import TabletIcon             from '@mui/icons-material/Tablet';
import AlternateEmailIcon     from '@mui/icons-material/AlternateEmail';
import SwapHorizIcon          from '@mui/icons-material/SwapHoriz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon       from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon     from '@mui/icons-material/HourglassEmpty';
import TimerIcon          from '@mui/icons-material/Timer';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import apiService, { authFetch } from '../../services/apiServices';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import BlockIcon from '@mui/icons-material/Block';
import ExtensionIcon from '@mui/icons-material/Extension';
import NotificationSettings from '../settings/NotificationSettings';
// Skedat e konfigurimit që deri tani jetonin te një faqe pa asnjë hyrje te
// menyja (`/work-types`): rregullat e orarit, llojet e pushimeve, ndërprerja
// dhe fjalori i llojeve të punës. Tani janë këtu, te i vetmi «Konfigurim».
import { OrariTab as RregullatOraritTab, PushimetTab as LlojetPushimitTab, NderprerjaTab } from '../../pages/SettingsPage';
import WorkingTypesTab from '../settings/WorkingTypesTab';
import { SEVERITY_LEVELS, severityMeta, matchesQuery } from '../../config/warnings';
import PageHeader        from '../common/PageHeader';
import Toast            from '../actions/Toasts';
import config            from '../../config/config';
import { useModuleSettings } from '../../hooks/useModuleSettings';
import { accountStatus, accountStatusChip, formatDMY } from '../../utils/activationStatus';

// ─── constants ───────────────────────────────────────────────────────────────
const EMAIL_DEFAULTS = {
    MONITORING_EMAIL: '',
    ARCHIVE_EMAIL: '',
};
const BRAND_COLOR_DEFAULT = '#3E8FA3';
const BRAND_COLORS = ['#3E8FA3', '#2563EB', '#6DB5C8', '#F59E0B', '#EF4444', '#6DB5C8', '#111827'];

const CUSTOM_ROLE_COLORS = [
  '#DC2626','#D97706','#CA8A04','#16A34A','#059669',
  '#0891B2','#2563EB','#7C3AED','#9333EA','#DB2777',
  '#6B7280','#111827',
];

const BLANK_CUSTOM_ROLE = {
  name: '', description: '', color: '#7C3AED', scope: 'company', permissions: {},
};


const ROLE_LABELS = { admin: 'Admin', manager: 'Menaxher', hr: 'HR', payroll: 'Payroll', employee: 'Punëtor' };
const ROLE_COLORS = { admin: 'error', manager: 'warning', hr: 'primary', payroll: 'secondary', employee: 'success' };

const ROLES_RBAC = [
  { id: 'admin',   name: 'Administrator', color: '#DC2626', desc: 'Qasje totale: konfigurim, fshirje, export, RBAC' },
  { id: 'hr',      name: 'HR Manager',    color: '#D97706', desc: 'Punëtorë, kontratat, pushimet, pagat, trajnimet' },
  { id: 'payroll', name: 'Payroll',       color: '#3E8FA3', desc: 'Vetëm pagat, eksport ATK/KBPK' },
  { id: 'manager', name: 'Menaxher',      color: '#0891B2', desc: 'Shikon ekipin e vet, aprovat pushimet & orët shtesë' },
  { id: 'employee',name: 'Punëtor',       color: '#3E8FA3', desc: 'Vetëm të dhënat e veta: slip paga, pushime, orari' },
];

const PERMISSIONS = [
  { section: 'Punëtorët',  admin: true,  hr: true,  payroll: false,     manager: 'read',    employee: 'self' },
  { section: 'Kontratat',  admin: true,  hr: true,  payroll: false,     manager: 'read',    employee: 'self' },
  { section: 'Pagat',      admin: true,  hr: true,  payroll: true,      manager: false,     employee: 'slip' },
  { section: 'Pushimet',   admin: true,  hr: true,  payroll: false,     manager: 'approve', employee: 'request' },
  { section: 'Prezenca',   admin: true,  hr: true,  payroll: false,     manager: 'team',    employee: 'self' },
  { section: 'Trajnimet',  admin: true,  hr: true,  payroll: false,     manager: 'read',    employee: 'read' },
  { section: 'Raportet',   admin: true,  hr: true,  payroll: 'limited', manager: 'team',    employee: false },
  { section: 'Compliance', admin: true,  hr: true,  payroll: false,     manager: false,     employee: false },
  { section: 'Konfigurim', admin: true,  hr: false, payroll: false,     manager: false,     employee: false },
  { section: 'Audit Log',  admin: true,  hr: 'read',payroll: false,     manager: false,     employee: false },
];

// ─── Privilege system — opsione specifike për çdo modul ──────────────────────
const MODULE_DEFS = [
  {
    key: 'workers', label: 'Punëtorët', group: 'HR',
    levels: [
      { value: 'none', label: 'Asnjë',       color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa asnjë qasje në listën e punëtorëve' },
      { value: 'self', label: 'Vetë',         color: '#0891B2', bg: '#E0F2FE', desc: 'Shikon vetëm profilin e vet' },
      { value: 'team', label: 'Ekipi i vet',  color: '#D97706', bg: '#FEF3C7', desc: 'Shikon punëtorët e ekipit të vet' },
      { value: 'full', label: 'Të gjithë',    color: '#16A34A', bg: '#DCFCE7', desc: 'Qasje e plotë: shiko + edito + shto të gjithë punëtorët' },
    ],
  },
  {
    key: 'contracts', label: 'Kontratat', group: 'HR',
    levels: [
      { value: 'none', label: 'Asnjë',       color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në kontratat' },
      { value: 'self', label: 'Kontrata vetë', color: '#0891B2', bg: '#E0F2FE', desc: 'Shikon vetëm kontratën e vet' },
      { value: 'team', label: 'Ekipi',        color: '#D97706', bg: '#FEF3C7', desc: 'Shikon kontratat e ekipit të vet' },
      { value: 'full', label: 'Të gjitha',    color: '#16A34A', bg: '#DCFCE7', desc: 'Shiko + krijo + ndrysho të gjitha kontratat' },
    ],
  },
  {
    key: 'salaries', label: 'Pagat', group: 'Financa',
    levels: [
      { value: 'none', label: 'Asnjë',        color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në pagat' },
      { value: 'slip', label: 'Slip vetë',     color: '#0891B2', bg: '#E0F2FE', desc: 'Shikon vetëm slip-in e pagës personale' },
      { value: 'team', label: 'Ekipi i vet',  color: '#D97706', bg: '#FEF3C7', desc: 'Shikon pagat e ekipit të vet, pa i ndryshuar' },
      { value: 'full', label: 'Të gjitha',    color: '#16A34A', bg: '#DCFCE7', desc: 'Pagat e të gjithëve + llogaritje + eksport ATK/KBPK' },
    ],
  },
  {
    key: 'holidays', label: 'Pushimet', group: 'HR',
    levels: [
      { value: 'none',    label: 'Asnjë',          color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në pushime' },
      { value: 'request', label: 'Kërko vetë',      color: '#0891B2', bg: '#E0F2FE', desc: 'Mund të kërkojë pushim vetëm për vete' },
      { value: 'approve', label: 'Aprovo ekip',     color: '#D97706', bg: '#FEF3C7', desc: 'Aprovat / refuzon pushimet e ekipit të vet' },
      { value: 'full',    label: 'Menaxho gjithë',  color: '#16A34A', bg: '#DCFCE7', desc: 'Aprovat pushimet e të gjithëve + konfiguro llojet' },
    ],
  },
  {
    key: 'presence', label: 'Prezenca', group: 'HR',
    levels: [
      { value: 'none', label: 'Asnjë',        color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në prezencë' },
      { value: 'self', label: 'Vetë',          color: '#0891B2', bg: '#E0F2FE', desc: 'Check-in/out dhe historia personale' },
      { value: 'team', label: 'Ekipi i vet',  color: '#D97706', bg: '#FEF3C7', desc: 'Shikon prezencën e ekipit të vet, live' },
      { value: 'full', label: 'Të gjithë',    color: '#16A34A', bg: '#DCFCE7', desc: 'Prezenca e të gjithëve + raportet + korrigjimi manual' },
    ],
  },
  {
    key: 'trainings', label: 'Trajnimet', group: 'HR',
    levels: [
      { value: 'none', label: 'Asnjë',         color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në trajnime' },
      { value: 'self', label: 'Vetë',           color: '#0891B2', bg: '#E0F2FE', desc: 'Shikon trajnimet e veta dhe regjistrohet' },
      { value: 'team', label: 'Ekipi i vet',   color: '#D97706', bg: '#FEF3C7', desc: 'Shikon trajnimet e ekipit, nuk i edito' },
      { value: 'full', label: 'Menaxho',        color: '#16A34A', bg: '#DCFCE7', desc: 'Shton, edito, fshi trajnime + cakton pjesëmarrës' },
    ],
  },
  {
    key: 'reports', label: 'Raportet', group: 'Analitikë',
    levels: [
      { value: 'none', label: 'Asnjë',          color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa asnjë raport' },
      { value: 'self', label: 'Vetë',            color: '#0891B2', bg: '#E0F2FE', desc: 'Vetëm raportet personale (slip paga, pushimet e veta)' },
      { value: 'team', label: 'Ekipi i vet',    color: '#D97706', bg: '#FEF3C7', desc: 'Raportet e ekipit të vet (prezenca, pagat, pushimet)' },
      { value: 'full', label: 'Të gjitha',      color: '#16A34A', bg: '#DCFCE7', desc: 'Raportet e të gjithëve + eksport Excel/PDF + ATK' },
    ],
  },
  {
    key: 'compliance', label: 'Compliance', group: 'Analitikë',
    levels: [
      { value: 'none', label: 'Asnjë',    color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në compliance' },
      { value: 'read', label: 'Shiko',    color: '#0891B2', bg: '#E0F2FE', desc: 'Shikon skor-et dhe raportet e compliance' },
      { value: 'full', label: 'Menaxho',  color: '#16A34A', bg: '#DCFCE7', desc: 'Ndrysho rregullat, cakto detyrat, menaxho auditimin' },
    ],
  },
  {
    key: 'settings', label: 'Konfigurim', group: 'Sistemi',
    levels: [
      { value: 'none', label: 'Asnjë',   color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në konfigurime' },
      { value: 'read', label: 'Shiko',   color: '#0891B2', bg: '#E0F2FE', desc: 'Shikon konfigurimet, por nuk i ndryshon' },
      { value: 'full', label: 'Admin',   color: '#DC2626', bg: '#FEF2F2', desc: 'Qasje totale: ndrysho të gjitha konfigurimet e kompanisë' },
    ],
  },
  {
    key: 'audit', label: 'Audit Log', group: 'Sistemi',
    levels: [
      { value: 'none',   label: 'Asnjë',    color: '#9CA3AF', bg: '#F9FAFB', desc: 'Pa qasje në audit log' },
      { value: 'read',   label: 'Shiko',    color: '#0891B2', bg: '#E0F2FE', desc: 'Lexon historikun e veprimeve në sistem' },
      { value: 'export', label: 'Eksport',  color: '#16A34A', bg: '#DCFCE7', desc: 'Shikon + eksporton log-et e auditit si Excel/PDF' },
    ],
  },
];

// For backward compatibility and the PERMISSIONS matrix
const ACCESS_LEVELS = [
  { value: 'none',  label: 'Asnjë', color: '#9CA3AF', bg: '#F9FAFB' },
  { value: 'read',  label: 'Shiko', color: '#0891B2', bg: '#E0F2FE' },
  { value: 'write', label: 'Edito', color: '#D97706', bg: '#FEF3C7' },
  { value: 'full',  label: 'Plota', color: '#16A34A', bg: '#DCFCE7' },
];

const ROLE_DEFAULT_PERMS = {
  admin:    { workers:'full',    contracts:'full',  salaries:'full',  holidays:'full',    presence:'full',  trainings:'full',  reports:'full',    compliance:'full',  settings:'full',  audit:'export' },
  hr:       { workers:'full',    contracts:'full',  salaries:'team',  holidays:'full',    presence:'full',  trainings:'full',  reports:'full',    compliance:'read',  settings:'none',  audit:'read'   },
  payroll:  { workers:'none',    contracts:'none',  salaries:'full',  holidays:'none',    presence:'none',  trainings:'none',  reports:'team',    compliance:'none',  settings:'none',  audit:'none'   },
  manager:  { workers:'team',    contracts:'team',  salaries:'none',  holidays:'approve', presence:'team',  trainings:'team',  reports:'team',    compliance:'none',  settings:'none',  audit:'none'   },
  employee: { workers:'self',    contracts:'self',  salaries:'slip',  holidays:'request', presence:'self',  trainings:'self',  reports:'self',    compliance:'none',  settings:'none',  audit:'none'   },
  staff:    { workers:'self',    contracts:'self',  salaries:'slip',  holidays:'request', presence:'self',  trainings:'self',  reports:'self',    compliance:'none',  settings:'none',  audit:'none'   },
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const boolFromValue = (value, fallback = false) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number')  return value !== 0;
    if (typeof value === 'string')  return ['true','1','yes','on'].includes(value.trim().toLowerCase());
    return fallback;
};

const normalizeRoleValue = (value = '') => {
    const map = {
        admin:'admin', administrator:'admin',
        manager:'manager', menaxher:'manager',
        hr:'hr', 'hr manager':'hr',
        payroll:'payroll',
        employee:'employee', punetor:'employee', 'punëtor':'employee',
        staff:'staff',
    };
    return map[(value?.toString().trim().toLowerCase() || '')] || 'staff';
};

const DEFAULT_ROLES = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

const parsePermissions = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return null; }
};

const normalizeUser = (row = {}) => {
    const role = normalizeRoleValue(row.user_role || row.UserRole || row.role || row.Role || 'staff');
    return {
        id: row.id || row.Id,
        userName: row.user_name || row.UserName || row.email || row.Email || '',
        email: row.email || row.Email || '',
        firstName: row.firstname || row.firstName || row.name || row.Name || '',
        lastName: row.lastname || row.lastName || row.surname || row.Surname || '',
        role, originalRole: role,
        phone: row.phone || row.Phone || '',
        jobTitle: row.job_title || row.JobTitle || row.position || row.Position || '',
        notes: row.notes || row.Notes || '',
        active: row.active !== undefined ? row.active : row.Active !== undefined ? row.Active : true,
        worker_id: row.worker_id || row.WorkerId || null,
        permissions: parsePermissions(row.permissions || row.Permissions),
        // Data e aktivizimit e punëtorit të lidhur + verdikti i serverit («sot» i kompanisë).
        activationDate: row.activation_date || row.ActivationDate || null,
        activationStatus: row.activation_status || row.ActivationStatus || null,
    };
};

const BLANK_USER = { userName:'', email:'', firstName:'', lastName:'', role:'staff', phone:'', jobTitle:'', sendInvite:true };

// ─── Permission Editor component — opsione specifike për çdo modul ───────────
/**
 * Njoftimi i një tabi — një vend i vetëm në vend të gjashtë kopjeve.
 *
 * Gjashtë tabe mbanin `<Snackbar message={snack}>` pa asnjë shenjë ashpërsie:
 * "Departamenti u fshi." dhe "Emri i departamentit është i detyrueshëm!" dilnin
 * si i njëjti drejtkëndësh gri. Pra një ruajtje që DËSHTOI dukej saktësisht si
 * një ruajtje që kaloi — dhe përdoruesi largohej nga faqja pa e ditur.
 *
 * `say(mesazh)` është sukses; `say(mesazh, 'error')` është gabim. Pamja vjen nga
 * `Toast`, i njëjti komponent si kudo tjetër në aplikacion.
 */
function useNotice() {
    const [notice, setNotice] = useState({ open: false, message: '', variant: 'success' });
    const say = useCallback((message, variant = 'success') => {
        if (!message) return;
        setNotice({ open: true, message, variant });
    }, []);
    const element = (
        <Toast open={notice.open} message={notice.message} variant={notice.variant}
            onClose={() => setNotice(n => ({ ...n, open: false }))} />
    );
    return { say, notice: element };
}

function PermissionEditor({ permissions, onChange }) {
    const perms = permissions || {};
    const groups = [...new Set(MODULE_DEFS.map(m => m.group))];

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
            {groups.map((group, gi) => (
                <Box key={group}>
                    {/* Group header */}
                    <Box sx={{
                        px: 1.5, py: 0.6,
                        bgcolor: 'grey.50',
                        borderTop: gi > 0 ? '1px solid' : 'none',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}>
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.disabled' }}>
                            {group}
                        </Typography>
                    </Box>

                    {MODULE_DEFS.filter(m => m.group === group).map((mod, i, arr) => {
                        const current = perms[mod.key] || 'none';
                        const currentLevel = mod.levels.find(l => l.value === current) || mod.levels[0];

                        return (
                            <Box
                                key={mod.key}
                                sx={{
                                    px: 1.5, py: 0.9,
                                    borderBottom: i < arr.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    transition: 'background .1s',
                                }}
                            >
                                {/* Module name + current description */}
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: 12, color: 'text.primary', minWidth: 90 }}>
                                        {mod.label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: 11, color: currentLevel.color, fontStyle: 'italic', textAlign: 'right', flex: 1, ml: 1 }}>
                                        {currentLevel.desc}
                                    </Typography>
                                </Stack>

                                {/* Level pills */}
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {mod.levels.map(l => {
                                        const selected = current === l.value;
                                        return (
                                            <Tooltip key={l.value} title={l.desc} placement="top" arrow>
                                                <Box
                                                    onClick={() => onChange(mod.key, l.value)}
                                                    sx={{
                                                        px: 1.25, py: 0.3,
                                                        borderRadius: 10,
                                                        cursor: 'pointer',
                                                        fontSize: 11,
                                                        fontWeight: selected ? 700 : 500,
                                                        border: '1.5px solid',
                                                        borderColor: selected ? l.color : '#D1D5DB',
                                                        bgcolor: selected ? l.color : 'transparent',
                                                        color: selected ? '#fff' : l.color,
                                                        transition: 'all .15s ease',
                                                        userSelect: 'none',
                                                        whiteSpace: 'nowrap',
                                                        '&:hover': {
                                                            borderColor: l.color,
                                                            bgcolor: selected ? l.color : l.bg,
                                                            color: selected ? '#fff' : l.color,
                                                        },
                                                    }}
                                                >
                                                    {selected && '✓ '}{l.label}
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}

// ─── Shared sub-components ───────────────────────────────────────────────────
function FieldLabel({ children }) {
    return <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block', mb:0.5 }}>{children}</Typography>;
}

function SectionBox({ title, icon, children }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                {icon}
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">{title}</Typography>
            </Stack>
            {children}
        </Box>
    );
}

function SectionCard({ title, icon, children, action }) {
    return (
        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{ p: '10px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {icon}
                    <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
                </Stack>
                {action}
            </Box>
            <CardContent sx={{ p: '16px !important' }}>{children}</CardContent>
        </Card>
    );
}

function PermCell({ val }) {
    if (val === true)  return <CheckCircleIcon sx={{ fontSize: 18, color: '#3E8FA3' }} />;
    if (val === false) return <Typography variant="caption" color="text.disabled">—</Typography>;
    return <Chip size="small" label={val} sx={{ fontSize: 10, height: 18 }} />;
}

// Punëtorët vijnë me FirstName/LastName; Name/Surname janë emrat e vjetër.
const fullName = (w) => `${w.FirstName || w.Name || w.name || ''} ${w.LastName || w.Surname || w.surname || ''}`.trim();

// ─── Tab: Fiskal ─────────────────────────────────────────────────────────────
function FiskalTab() {
    const [policies, setPolicies] = useState([]);
    useEffect(() => {
        apiService().get('fiscalPoliciesExt').then(r => { if (r.success) setPolicies(r.data); });
    }, []);
    const active = policies.find(p => p.Active);
    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Konfigurimet fiskale menaxhohen te <strong>Politikat Fiskale</strong>. Këtu shihni një rezyme dhe lidhje të shpejtë.
            </Alert>
            {active && (
                <SectionCard title="Politika aktive" icon={<AccountBalanceIcon sx={{ fontSize:18, color:'primary.main' }} />}
                    action={<Button size="small" component={Link} to="/fiscal" endIcon={<ArrowForwardIcon />} sx={{ textTransform:'none' }}>Menaxho</Button>}>
                    <Grid container spacing={2}>
                        {[
                            { label:'Pensioni (punëtor)',     value:`${active.PensionEmployee ?? 5}%`,   color:'#3E8FA3', bg:'#EEF2FF' },
                            { label:'Pensioni (punëdhënës)', value:`${active.PensionEmployer ?? 5}%`,   color:'#3E8FA3', bg:'#ECFDF5' },
                            { label:'Paga minimale',          value:`${active.MinimumWage ?? 264} €`,    color:'#D97706', bg:'#FFFBEB' },
                            { label:'Orë shtesë maks/javë',  value:`${active.MaxOvertimeHoursWeek ?? active.MaxOvertimeHours ?? 8}h/javë`, color:'#DC2626', bg:'#FEF2F2' },
                        ].map(s => (
                            <Grid item xs={6} sm={3} key={s.label}>
                                <Box sx={{ bgcolor: s.bg, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ color: s.color, fontWeight: 700, display: 'block' }}>{s.label}</Typography>
                                    <Typography variant="h6" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        <PercentIcon sx={{ fontSize: 12, mr: 0.5 }} />Shkallët tatimore (Kosovë)
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {[['0–250€','0%'],['250–450€','8%'],['>450€','10%']].map(([r,t]) => (
                            <Chip key={r} label={`${r} → ${t}`} size="small" variant="outlined" color="info" />
                        ))}
                    </Stack>
                </SectionCard>
            )}
            <SectionCard title="Të gjitha politikat" icon={<AccountBalanceIcon sx={{ fontSize:18, color:'text.secondary' }} />}
                action={<Button size="small" variant="contained" component={Link} to="/fiscal/addfiscal" startIcon={<AddIcon />} sx={{ textTransform:'none' }}>Politikë e re</Button>}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            {['Emri','Vlefshme nga','Deri','Pension %','Statusi',''].map(h => (
                                <TableCell key={h} sx={{ fontWeight:700 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {policies.map(p => (
                            <TableRow key={p.Id} hover>
                                <TableCell>{p.Name}</TableCell>
                                <TableCell>{p.ValidFrom || '—'}</TableCell>
                                <TableCell>{p.ValidTo || 'Pa limit'}</TableCell>
                                <TableCell>{p.PensionEmployee ?? 5}% / {p.PensionEmployer ?? 5}%</TableCell>
                                <TableCell><Chip size="small" label={p.Active ? 'Aktive' : 'Joaktive'} color={p.Active ? 'success' : 'default'} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" component={Link} to={`/fiscal/editfiscal/${p.Id}`}><EditIcon sx={{ fontSize:16 }} /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </SectionCard>
        </Box>
    );
}

// ─── Tab: Dokumentet ─────────────────────────────────────────────────────────
function DokumentetTab() {
    const [docs, setDocs]     = useState([]);
    const [editDoc, setEditDoc] = useState(null);
    const { say, notice } = useNotice();

    const CATS = { identity:'Identitet', legal:'Juridik', health:'Shëndet', education:'Arsim', hr:'HR', financial:'Financiar' };

    const load = useCallback(() => {
        apiService().get('documentSettings').then(r => { if (r.success) setDocs(r.data); }).catch(() => say('Nuk u ngarkuan dokumentet.', 'error'));
    }, []);
    useEffect(() => { load(); }, [load]);

    const saveDoc = async () => {
        if (!editDoc) return;
        if (editDoc.Id === 0) await apiService().post('documentSettings', { ...editDoc, Id: undefined });
        else                  await apiService().put('documentSettings', editDoc);
        setEditDoc(null); load(); say('Dokumenti u ruajt.');
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Konfiguro dokumentet e detyrueshme dhe opsionale për çdo punëtor.
            </Alert>
            <SectionCard title="Kërkesat e dokumenteve" icon={<FolderOpenIcon sx={{ fontSize:18, color:'#D97706' }} />}
                action={
                    <Button size="small" variant="contained" startIcon={<AddIcon />}
                        onClick={() => setEditDoc({ Id:0, Code:'', Name:'', Required:true, Category:'hr', ReminderDays:[30,15], AcceptedFormats:['pdf'] })}
                        sx={{ textTransform:'none' }}>
                        Shto dokument
                    </Button>
                }>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            {['Kodi','Emri','Kategoria','I detyrueshëm','Reminder (ditë para)','Formatet',''].map(h => (
                                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {docs.map(doc => (
                            <TableRow hover key={doc.Id}>
                                <TableCell><Chip label={doc.Code} size="small" variant="outlined" sx={{ fontFamily:'monospace', fontSize:11 }} /></TableCell>
                                <TableCell><Typography variant="body2" fontWeight={600}>{doc.Name}</Typography></TableCell>
                                <TableCell><Chip size="small" label={CATS[doc.Category] || doc.Category} variant="outlined" /></TableCell>
                                <TableCell><Chip size="small" label={doc.Required ? 'Detyrim' : 'Opsional'} color={doc.Required ? 'error' : 'default'} variant="outlined" /></TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {(doc.ReminderDays || []).length === 0
                                            ? <Typography variant="caption" color="text.disabled">—</Typography>
                                            : (doc.ReminderDays || []).map(d => <Chip key={d} label={`${d}d`} size="small" />)}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {(doc.AcceptedFormats || []).map(f => <Chip key={f} label={`.${f}`} size="small" variant="outlined" />)}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" onClick={() => setEditDoc({ ...doc, ReminderDays: [...(doc.ReminderDays || [])] })}>
                                        <EditIcon sx={{ fontSize:16 }} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </SectionCard>

            <Dialog open={!!editDoc} onClose={() => setEditDoc(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{editDoc?.Id === 0 ? 'Shto dokument' : 'Edito dokumentin'}</DialogTitle>
                <DialogContent>
                    {editDoc && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={6}><TextField size="small" fullWidth label="Kodi" value={editDoc.Code} onChange={e => setEditDoc(p => ({ ...p, Code: e.target.value }))} helperText="p.sh. id_card" /></Grid>
                            <Grid item xs={6}><TextField size="small" fullWidth label="Emri" value={editDoc.Name} onChange={e => setEditDoc(p => ({ ...p, Name: e.target.value }))} /></Grid>
                            <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Kategoria</InputLabel>
                                    <Select label="Kategoria" value={editDoc.Category} onChange={e => setEditDoc(p => ({ ...p, Category: e.target.value }))}>
                                        {Object.entries(CATS).map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}><TextField size="small" fullWidth label="Formatet (me presje)" value={(editDoc.AcceptedFormats || []).join(',')} onChange={e => setEditDoc(p => ({ ...p, AcceptedFormats: e.target.value.split(',').map(x => x.trim()).filter(Boolean) }))} helperText="p.sh. pdf,jpg,png" /></Grid>
                            <Grid item xs={12}><TextField size="small" fullWidth label="Reminderat (ditë para skadimit)" value={(editDoc.ReminderDays || []).join(',')} onChange={e => setEditDoc(p => ({ ...p, ReminderDays: e.target.value.split(',').map(x => +x.trim()).filter(n => n > 0) }))} helperText="p.sh. 60,30,15" /></Grid>
                            <Grid item xs={12}><FormControlLabel control={<Switch checked={editDoc.Required} onChange={e => setEditDoc(p => ({ ...p, Required: e.target.checked }))} />} label="Dokument i detyrueshëm" /></Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDoc(null)}>Anulo</Button>
                    <Button variant="contained" onClick={saveDoc}>Ruaj</Button>
                </DialogActions>
            </Dialog>

            {notice}
        </Box>
    );
}

// ─── Tab: Compliance ─────────────────────────────────────────────────────────
function ComplianceTab() {
    const [cfg, setCfg] = useState(null);
    const { say, notice } = useNotice();

    useEffect(() => {
        apiService().get('complianceSettings').then(r => { if (r.success && r.data?.length) setCfg(r.data[0]); }).catch(() => say('Nuk u ngarkua konfigurimi.', 'error'));
    }, []);

    const save = async () => {
        await apiService().put('complianceSettings', { ...cfg, UpdatedAt: new Date().toISOString() });
        say('Compliance settings u ruajt!');
    };

    if (!cfg) return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
                Konfigurimet e Compliance nuk janë gjendet. Kontrolloni lidhjen me serverin.
            </Alert>
        </Box>
    );
    const totalWeight = (cfg.Rules || []).reduce((s, r) => s + r.weight, 0);

    return (
        <Box>
            <Alert severity={totalWeight !== 100 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                Totali i peshave: <strong>{totalWeight}/100</strong>
                {totalWeight !== 100 && ' — Peshat duhet të jenë saktësisht 100!'}
            </Alert>

            <SectionCard title="Rregullat & peshat e compliance" icon={<VerifiedUserIcon sx={{ fontSize:18, color:'#3E8FA3' }} />}
                action={<Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={save} sx={{ textTransform:'none' }}>Ruaj</Button>}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            {['Rregulli','Pesha (%)','Kritik','I lartë','Mesatar',''].map(h => (
                                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(cfg.Rules || []).map((rule, i) => (
                            <TableRow hover key={rule.id}>
                                <TableCell><Typography variant="body2" fontWeight={600}>{rule.name}</Typography></TableCell>
                                <TableCell sx={{ width:100 }}>
                                    <TextField type="number" size="small" value={rule.weight}
                                        onChange={e => setCfg(p => ({ ...p, Rules: p.Rules.map((r,j) => j===i ? { ...r, weight: +e.target.value } : r) }))}
                                        inputProps={{ min:0, max:100 }} sx={{ width:70 }}
                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                                </TableCell>
                                {['critical','high','medium'].map(level => (
                                    <TableCell key={level} sx={{ width:90 }}>
                                        <TextField type="number" size="small" value={rule.threshold?.[level] ?? 0}
                                            onChange={e => setCfg(p => ({ ...p, Rules: p.Rules.map((r,j) => j===i ? { ...r, threshold: { ...r.threshold, [level]: +e.target.value } } : r) }))}
                                            sx={{ width:70 }} />
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <Box sx={{ flex:1, height:6, bgcolor:'grey.200', borderRadius:3, overflow:'hidden', width:60 }}>
                                        <Box sx={{ height:'100%', width:`${rule.weight}%`, bgcolor:'#3E8FA3', borderRadius:3 }} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </SectionCard>

            <SectionCard title="Nivelet e riskut" icon={<VerifiedUserIcon sx={{ fontSize:18, color:'text.secondary' }} />}>
                <Grid container spacing={1.5}>
                    {(cfg.Levels || []).map(level => (
                        <Grid item xs={6} sm={3} key={level.id}>
                            <Box sx={{ p:1.5, borderRadius:1.5, border:`2px solid ${level.color}`, textAlign:'center' }}>
                                <Typography variant="caption" sx={{ color:level.color, fontWeight:800, display:'block' }}>{level.label}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize:11 }}>{level.minScore}–{level.maxScore} pikë</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel control={<Switch checked={cfg.AutoRecalculate} onChange={e => setCfg(p => ({ ...p, AutoRecalculate: e.target.checked }))} />} label="Ri-kalkulo automatikisht" />
                    {cfg.AutoRecalculate && (
                        <TextField size="small" type="number" label="Intervali (orë)" value={cfg.RecalcIntervalHours}
                            onChange={e => setCfg(p => ({ ...p, RecalcIntervalHours: +e.target.value }))}
                            sx={{ width:150 }} InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
                    )}
                </Stack>
            </SectionCard>

            {notice}
        </Box>
    );
}

// ─── Tab: Siguria ─────────────────────────────────────────────────────────────
function SiguriTab() {
    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Rolet dhe privilegjet menaxhohen edhe te <strong>Përdoruesit & rolet</strong> (tab më lart). Kjo tabelë tregon rezyme të qasjes sipas rolit.
            </Alert>

            <SectionCard title="Rolet & privilegjet (RBAC)" icon={<SecurityIcon sx={{ fontSize:18, color:'#3E8FA3' }} />}>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {ROLES_RBAC.map(r => (
                        <Grid item xs={12} sm={4} md key={r.id}>
                            <Box sx={{ p:1.5, borderRadius:1.5, border:`1.5px solid ${r.color}20`, bgcolor:`${r.color}08` }}>
                                <Typography variant="caption" sx={{ color:r.color, fontWeight:800, display:'block' }}>{r.name}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize:11 }}>{r.desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight:700 }}>Seksioni</TableCell>
                                {ROLES_RBAC.map(r => (
                                    <TableCell key={r.id} align="center" sx={{ fontWeight:700, color:r.color, fontSize:12 }}>{r.name}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {PERMISSIONS.map(p => (
                                <TableRow hover key={p.section}>
                                    <TableCell><Typography variant="body2" fontWeight={600}>{p.section}</Typography></TableCell>
                                    {ROLES_RBAC.map(r => (
                                        <TableCell key={r.id} align="center"><PermCell val={p[r.id]} /></TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </SectionCard>

            <SectionCard title="Informacion GDPR" icon={<InfoOutlinedIcon sx={{ fontSize:18, color:'info.main' }} />}>
                <Grid container spacing={1.5}>
                    {[
                        { title:'Minimizimi i të dhënave', desc:'Çdo rol sheh vetëm atë që i nevojitet për funksionin e tij.' },
                        { title:'Audit trail', desc:'Çdo veprim regjistrohet në Audit Log me user, IP dhe timestamp.' },
                        { title:'Pseudonimizimi', desc:'Nr. bankar shfaqet i maskuar (****1234) tek rolet pa qasje financiare.' },
                        { title:'E drejta e fshirjes', desc:'Administratori mund të anonimizojë të dhënat pas largimit të punëtorit.' },
                    ].map(item => (
                        <Grid item xs={12} sm={6} key={item.title}>
                            <Box sx={{ p:1.5, bgcolor:'info.50', borderRadius:1.5, border:'1px solid', borderColor:'info.200' }}>
                                <Typography variant="caption" fontWeight={700} color="info.dark" sx={{ display:'block' }}>{item.title}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </SectionCard>
        </Box>
    );
}

// ─── Tab: Filialet ────────────────────────────────────────────────────────────
function BranchetTab() {
    const [branches, setBranches] = useState([]);
    const [workers,  setWorkers]  = useState([]);
    const [editItem, setEditItem] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const { say, notice } = useNotice();

    const load = useCallback(() => {
        apiService().get('branches').then(r => { if (r.success) setBranches(r.data); }).catch(() => say('Nuk u ngarkuan filialet.', 'error'));
        apiService().get('workers').then(r => { if (r.success) setWorkers(r.data); }).catch(() => say('Nuk u ngarkuan punëtorët.', 'error'));
    }, []);
    useEffect(() => { load(); }, [load]);

    const managerName = (id) => {
        const w = workers.find(w => w.Id === id || w.id === id);
        return w ? fullName(w) : '—';
    };

    const BLANK = { Id:0, Name:'', Code:'', Address:'', City:'', Phone:'', Email:'', ManagerId:'', Active:true, IsHeadquarters:false };

    const save = async () => {
        if (!editItem?.Name?.trim()) { say('Emri i filialit është i detyrueshëm!', 'error'); return; }
        if (editItem.Id === 0) await apiService().post('branches', { ...editItem, Id:undefined, CreatedAt: new Date().toISOString().split('T')[0] });
        else                   await apiService().put('branches', editItem);
        setEditItem(null); load(); say(editItem.Id === 0 ? 'Filiali u shtua.' : 'Filiali u përditësua.');
    };

    const handleDelete = async () => {
        await apiService().delete('branches', deleteId);
        setDeleteId(null); load(); say('Filiali u fshi.');
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Menaxho filialet dhe zyrat rajonale të kompanisë. Çdo filial mund të ketë punëtorë dhe departamente të veta.
            </Alert>

            <SectionCard
                title={`Filialet (${branches.length})`}
                icon={<StorefrontIcon sx={{ fontSize:18, color:'#3E8FA3' }} />}
                action={
                    <Button size="small" variant="contained" startIcon={<AddIcon />}
                        onClick={() => setEditItem({ ...BLANK })} sx={{ textTransform:'none' }}>
                        Filial i ri
                    </Button>
                }>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor:'grey.50' }}>
                            {['Kodi','Emri','Qyteti','Telefoni','Email','Menaxheri','Statusi',''].map(h => (
                                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {branches.map(b => (
                            <TableRow hover key={b.Id}>
                                <TableCell>
                                    <Chip label={b.Code} size="small" variant="outlined"
                                        color={b.IsHeadquarters ? 'primary' : 'default'}
                                        sx={{ fontFamily:'monospace', fontWeight:700, fontSize:11 }} />
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        {b.IsHeadquarters && <Chip size="small" label="HQ" color="primary" sx={{ fontSize:9, height:16 }} />}
                                        <Typography variant="body2" fontWeight={600}>{b.Name}</Typography>
                                    </Stack>
                                    {b.Address && <Typography variant="caption" color="text.secondary" sx={{ display:'flex', alignItems:'center', gap:0.25 }}><LocationOnIcon sx={{ fontSize:11 }} />{b.Address}</Typography>}
                                </TableCell>
                                <TableCell><Typography variant="body2">{b.City || '—'}</Typography></TableCell>
                                <TableCell>
                                    {b.Phone
                                        ? <Stack direction="row" alignItems="center" spacing={0.5}><PhoneIcon sx={{ fontSize:13, color:'text.secondary' }} /><Typography variant="caption">{b.Phone}</Typography></Stack>
                                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                                </TableCell>
                                <TableCell><Typography variant="caption" color="text.secondary">{b.Email || '—'}</Typography></TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <BadgeIcon sx={{ fontSize:13, color:'text.secondary' }} />
                                        <Typography variant="caption">{managerName(b.ManagerId)}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={b.Active ? 'Aktiv' : 'Joaktiv'}
                                        color={b.Active ? 'success' : 'default'} variant="outlined" />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={0.25}>
                                        <Tooltip title="Edito">
                                            <IconButton size="small" onClick={() => setEditItem({ ...b })}>
                                                <EditIcon sx={{ fontSize:16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        {!b.IsHeadquarters && (
                                            <Tooltip title="Fshi">
                                                <span>
                                                    <IconButton size="small" color="error" onClick={() => setDeleteId(b.Id)}>
                                                        <DeleteIcon sx={{ fontSize:16 }} />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        {branches.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py:3 }}>
                                    <Typography variant="body2" color="text.secondary">Nuk ka filiale të shtuara.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </SectionCard>

            {/* Summary cards */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                    { label:'Gjithsej filiale', value: branches.length, color:'#3E8FA3', bg:'#EEF2FF' },
                    { label:'Aktive',            value: branches.filter(b => b.Active).length, color:'#3E8FA3', bg:'#ECFDF5' },
                    { label:'Joaktive',          value: branches.filter(b => !b.Active).length, color:'#6B7280', bg:'#F3F4F6' },
                    { label:'Qytete',            value: [...new Set(branches.map(b => b.City).filter(Boolean))].length, color:'#D97706', bg:'#FFFBEB' },
                ].map(s => (
                    <Grid item xs={6} sm={3} key={s.label}>
                        <Box sx={{ p:1.5, borderRadius:1.5, bgcolor:s.bg, textAlign:'center' }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color:s.color }}>{s.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Edit dialog */}
            <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
                <DialogTitle sx={{ fontWeight:700, fontSize:'1rem', pb:1 }}>
                    {editItem?.Id === 0 ? 'Shto filial të ri' : `Edito: ${editItem?.Name}`}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt:2 }}>
                    {editItem && (
                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <TextField size="small" fullWidth label="Emri i filialit *" value={editItem.Name}
                                    onChange={e => setEditItem(p => ({ ...p, Name: e.target.value }))} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField size="small" fullWidth label="Kodi (unik)" value={editItem.Code}
                                    onChange={e => setEditItem(p => ({ ...p, Code: e.target.value.toUpperCase() }))}
                                    helperText="p.sh. HQ, PZ" />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField size="small" fullWidth label="Adresa" value={editItem.Address || ''}
                                    onChange={e => setEditItem(p => ({ ...p, Address: e.target.value }))} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField size="small" fullWidth label="Qyteti" value={editItem.City || ''}
                                    onChange={e => setEditItem(p => ({ ...p, City: e.target.value }))} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField size="small" fullWidth label="Telefoni" value={editItem.Phone || ''}
                                    onChange={e => setEditItem(p => ({ ...p, Phone: e.target.value }))} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField size="small" fullWidth label="Email" type="email" value={editItem.Email || ''}
                                    onChange={e => setEditItem(p => ({ ...p, Email: e.target.value }))} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField select size="small" fullWidth label="Menaxheri i filialit" value={editItem.ManagerId || ''}
                                    onChange={e => setEditItem(p => ({ ...p, ManagerId: e.target.value }))}>
                                    <MenuItem value="">— Pa menaxher —</MenuItem>
                                    {workers.map(w => (
                                        <MenuItem key={w.Id || w.id} value={w.Id || w.id}>
                                            {fullName(w)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel control={
                                    <Switch checked={editItem.Active} onChange={e => setEditItem(p => ({ ...p, Active: e.target.checked }))} />
                                } label="Aktiv" />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel control={
                                    <Switch checked={editItem.IsHeadquarters} onChange={e => setEditItem(p => ({ ...p, IsHeadquarters: e.target.checked }))} />
                                } label="Selia qendrore (HQ)" />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p:2, gap:1 }}>
                    <Button onClick={() => setEditItem(null)} variant="outlined" sx={{ borderRadius:1.5, textTransform:'none' }}>Anulo</Button>
                    <Button onClick={save} variant="contained" startIcon={<SaveIcon />} sx={{ borderRadius:1.5, textTransform:'none' }}>Ruaj</Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Fshi filiarin</DialogTitle>
                <DialogContent><Typography>Ky filial do të fshihet. Ky veprim nuk kthehet mbrapsht.</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Anulo</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Fshi</Button>
                </DialogActions>
            </Dialog>

            {notice}
        </Box>
    );
}

// ─── Tab: Departamentet ───────────────────────────────────────────────────────
function DepartamentetTab() {
    const [depts,    setDepts]   = useState([]);
    const [branches, setBranches]= useState([]);
    const [workers,  setWorkers] = useState([]);
    const [editItem, setEditItem]= useState(null);
    const [deleteId, setDeleteId]= useState(null);
    const [filterBranch, setFilterBranch] = useState('all');
    const { say, notice } = useNotice();

    const load = useCallback(() => {
        apiService().get('departments').then(r => { if (r.success) setDepts(r.data); }).catch(() => say('Nuk u ngarkuan departamentet.', 'error'));
        apiService().get('branches').then(r => { if (r.success) setBranches(r.data); }).catch(() => say('Nuk u ngarkuan filialet.', 'error'));
        apiService().get('workers').then(r => { if (r.success) setWorkers(r.data); }).catch(() => say('Nuk u ngarkuan punëtorët.', 'error'));
    }, []);
    useEffect(() => { load(); }, [load]);

    const branchName = (id) => branches.find(b => String(b.Id) === String(id))?.Name || '—';
    // Një departament mund të jetë në disa filiale; rreshtat e vjetër kanë vetëm BranchId.
    const deptBranches = (d) => (Array.isArray(d.BranchIds) && d.BranchIds.length ? d.BranchIds : (d.BranchId ? [d.BranchId] : []));
    const workerName = (id) => {
        const w = workers.find(w => w.Id === id || w.id === id);
        return w ? fullName(w) : '—';
    };
    const parentName = (id) => id ? (depts.find(d => d.Id === id)?.Name || '—') : '—';

    const BLANK = { Id:0, Name:'', Code:'', BranchIds:[], ParentId:'', ManagerId:'', Description:'', Active:true };

    const save = async () => {
        if (!editItem?.Name?.trim()) { say('Emri i departamentit është i detyrueshëm!', 'error'); return; }
        const branchIds = (editItem.BranchIds || []).map(Number).filter(Boolean);
        const payload = {
            ...editItem,
            BranchIds: branchIds,
            BranchId:  branchIds[0] || null,
            ParentId:  editItem.ParentId   || null,
            ManagerId: editItem.ManagerId  || null,
        };
        if (editItem.Id === 0) await apiService().post('departments', { ...payload, Id:undefined, CreatedAt: new Date().toISOString().split('T')[0] });
        else                   await apiService().put('departments', payload);
        setEditItem(null); load(); say(editItem.Id === 0 ? 'Departamenti u shtua.' : 'Departamenti u përditësua.');
    };

    const handleDelete = async () => {
        await apiService().delete('departments', deleteId);
        setDeleteId(null); load(); say('Departamenti u fshi.');
    };

    const visible = filterBranch === 'all' ? depts : depts.filter(d => deptBranches(d).map(String).includes(String(filterBranch)));

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Organizo punëtorët sipas departamenteve. Një departament mund të jetë në disa filiale njëherësh (p.sh. Shitja në Prishtinë dhe në Gjakovë) dhe të ketë nën-departamente.
            </Alert>

            <SectionCard
                title={`Departamentet (${depts.length})`}
                icon={<ApartmentIcon sx={{ fontSize:18, color:'#3E8FA3' }} />}
                action={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField select size="small" value={filterBranch}
                            onChange={e => setFilterBranch(e.target.value)}
                            sx={{ minWidth:160 }} label="Filtro filialin">
                            <MenuItem value="all">Të gjitha</MenuItem>
                            {branches.map(b => <MenuItem key={b.Id} value={String(b.Id)}>{b.Name}</MenuItem>)}
                        </TextField>
                        <Button size="small" variant="contained" startIcon={<AddIcon />}
                            onClick={() => setEditItem({ ...BLANK })} sx={{ textTransform:'none', whiteSpace:'nowrap' }}>
                            Departament i ri
                        </Button>
                    </Stack>
                }>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor:'grey.50' }}>
                            {['Kodi','Emri','Filiali','Departamenti prind','Menaxheri','Statusi',''].map(h => (
                                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map(d => (
                            <TableRow hover key={d.Id}>
                                <TableCell>
                                    <Chip label={d.Code} size="small" variant="outlined"
                                        sx={{ fontFamily:'monospace', fontWeight:700, fontSize:11 }} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{d.Name}</Typography>
                                    {d.Description && <Typography variant="caption" color="text.secondary" sx={{ display:'block', maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{d.Description}</Typography>}
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        <StorefrontIcon sx={{ fontSize:13, color:'text.secondary' }} />
                                        {deptBranches(d).length
                                            ? deptBranches(d).map(bid => <Chip key={bid} size="small" label={branchName(bid)} variant="outlined" sx={{ fontSize:11, height:20 }} />)
                                            : <Typography variant="caption">—</Typography>}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    {d.ParentId
                                        ? <Chip size="small" label={parentName(d.ParentId)} variant="outlined" sx={{ fontSize:11 }} />
                                        : <Typography variant="caption" color="text.disabled">— (Kryesor)</Typography>}
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <BadgeIcon sx={{ fontSize:13, color:'text.secondary' }} />
                                        <Typography variant="caption">{workerName(d.ManagerId)}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={d.Active ? 'Aktiv' : 'Joaktiv'}
                                        color={d.Active ? 'success' : 'default'} variant="outlined" />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={0.25}>
                                        <Tooltip title="Edito">
                                            <IconButton size="small" onClick={() => setEditItem({ ...d, BranchIds: deptBranches(d), ParentId: d.ParentId || '', ManagerId: d.ManagerId || '' })}>
                                                <EditIcon sx={{ fontSize:16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Fshi">
                                            <span>
                                                <IconButton size="small" color="error" onClick={() => setDeleteId(d.Id)}>
                                                    <DeleteIcon sx={{ fontSize:16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        {visible.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py:3 }}>
                                    <Typography variant="body2" color="text.secondary">Nuk ka departamente.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </SectionCard>

            {/* Org chart summary */}
            <Grid container spacing={2} sx={{ mt:1 }}>
                {branches.map(b => {
                    const bDepts = depts.filter(d => deptBranches(d).map(String).includes(String(b.Id)));
                    return (
                        <Grid item xs={12} sm={6} md={4} key={b.Id}>
                            <Box sx={{ p:2, borderRadius:2, border:'1px solid', borderColor:'divider', bgcolor:'grey.50' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb:1 }}>
                                    <StorefrontIcon sx={{ fontSize:16, color:'primary.main' }} />
                                    <Typography variant="subtitle2" fontWeight={700}>{b.Name}</Typography>
                                    <Chip size="small" label={`${bDepts.length} dept`} color="primary" variant="outlined" sx={{ fontSize:10, height:18 }} />
                                </Stack>
                                <Stack spacing={0.5}>
                                    {bDepts.map(d => (
                                        <Stack key={d.Id} direction="row" alignItems="center" spacing={0.75} sx={{ pl: d.ParentId ? 2 : 0 }}>
                                            {d.ParentId && <Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:'grey.400', flexShrink:0 }} />}
                                            <Typography variant="caption" fontWeight={d.ParentId ? 400 : 600}>{d.Name}</Typography>
                                        </Stack>
                                    ))}
                                    {bDepts.length === 0 && <Typography variant="caption" color="text.disabled">Pa departamente</Typography>}
                                </Stack>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Edit dialog */}
            <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
                <DialogTitle sx={{ fontWeight:700, fontSize:'1rem', pb:1 }}>
                    {editItem?.Id === 0 ? 'Shto departament' : `Edito: ${editItem?.Name}`}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt:2 }}>
                    {editItem && (
                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <TextField size="small" fullWidth label="Emri i departamentit *" value={editItem.Name}
                                    onChange={e => setEditItem(p => ({ ...p, Name: e.target.value }))} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField size="small" fullWidth label="Kodi" value={editItem.Code || ''}
                                    onChange={e => setEditItem(p => ({ ...p, Code: e.target.value.toUpperCase() }))}
                                    helperText="p.sh. HR, IT" />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField size="small" fullWidth label="Përshkrimi" multiline minRows={2} value={editItem.Description || ''}
                                    onChange={e => setEditItem(p => ({ ...p, Description: e.target.value }))} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField select size="small" fullWidth label="Filialet" value={editItem.BranchIds || []}
                                    helperText="Zgjidh një ose më shumë filiale ku ekziston ky departament"
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (sel) => (
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {sel.map(id => <Chip key={id} size="small" label={branchName(id)} sx={{ height:20, fontSize:11 }} />)}
                                            </Stack>
                                        ),
                                    }}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setEditItem(p => ({ ...p, BranchIds: typeof v === 'string' ? v.split(',') : v }));
                                    }}>
                                    {branches.map(b => (
                                        <MenuItem key={b.Id} value={b.Id}>
                                            <Checkbox size="small" checked={(editItem.BranchIds || []).map(String).includes(String(b.Id))} sx={{ p:0.25, mr:1 }} />
                                            {b.Name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField select size="small" fullWidth label="Departamenti prind (nën-departament)" value={editItem.ParentId || ''}
                                    onChange={e => setEditItem(p => ({ ...p, ParentId: e.target.value }))}>
                                    <MenuItem value="">— Departament kryesor —</MenuItem>
                                    {depts.filter(d => d.Id !== editItem.Id).map(d => (
                                        <MenuItem key={d.Id} value={d.Id}>{d.Name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField select size="small" fullWidth label="Menaxheri i departamentit" value={editItem.ManagerId || ''}
                                    onChange={e => setEditItem(p => ({ ...p, ManagerId: e.target.value }))}>
                                    <MenuItem value="">— Pa menaxher —</MenuItem>
                                    {workers.map(w => (
                                        <MenuItem key={w.Id || w.id} value={w.Id || w.id}>
                                            {fullName(w)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel control={
                                    <Switch checked={editItem.Active} onChange={e => setEditItem(p => ({ ...p, Active: e.target.checked }))} />
                                } label="Aktiv" />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p:2, gap:1 }}>
                    <Button onClick={() => setEditItem(null)} variant="outlined" sx={{ borderRadius:1.5, textTransform:'none' }}>Anulo</Button>
                    <Button onClick={save} variant="contained" startIcon={<SaveIcon />} sx={{ borderRadius:1.5, textTransform:'none' }}>Ruaj</Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Fshi departamentin</DialogTitle>
                <DialogContent><Typography>Ky departament do të fshihet. Nën-departamentet mund të mbeten pa prind.</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Anulo</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Fshi</Button>
                </DialogActions>
            </Dialog>

            {notice}
        </Box>
    );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────
// ─── Tab: Kiosk QR ───────────────────────────────────────────────────────────
/** Kopjo një vlerë kredenciali, me konfirmim të shkurtër. */
function CopyValue({ label, value, mono = true }) {
    const [done, setDone] = React.useState(false);
    const copy = () => navigator.clipboard.writeText(String(value)).then(() => {
        setDone(true);
        setTimeout(() => setDone(false), 1500);
    });
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
            <Box sx={{ display:'flex', alignItems:'center', gap:1, bgcolor:'grey.50', borderRadius:1.5, px:1.5, py:1 }}>
                <Typography variant="body2" sx={{ flex:1, wordBreak:'break-all', fontSize:'0.82rem',
                    ...(mono ? { fontFamily:'monospace' } : {}) }}>
                    {value}
                </Typography>
                <Tooltip title={done ? 'U kopjua!' : 'Kopjo'}>
                    <IconButton size="small" onClick={copy} color={done ? 'success' : 'default'}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

/**
 * Pajisjet kiosk të kësaj kompanie.
 *
 * Kioskat jetojnë te baza e kompanisë: serveri e merr kompaninë nga sesioni i
 * adminit, ndaj këtu shfaqen dhe krijohen vetëm kioskat e tij. Kredencialet
 * (kompania, Device ID, token) shfaqen një herë — token-i s'ruhet i lexueshëm.
 */
function KioskDevicesCard() {
    const [devices, setDevices]   = React.useState([]);
    const [company, setCompany]   = React.useState('');
    const [loading, setLoading]   = React.useState(true);
    const [error, setError]       = React.useState('');
    const [newName, setNewName]   = React.useState('');
    const [addOpen, setAddOpen]   = React.useState(false);
    const [busy, setBusy]         = React.useState(false);
    const [creds, setCreds]       = React.useState(null); // { company, id, name, token }
    const [rotateId, setRotateId] = React.useState(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const r = await authFetch('/api/qr/kiosk/list');
            const d = await r.json().catch(() => ({}));
            if (!d.success) throw new Error(d.error || 'Lista s\'u ngarkua');
            setDevices(d.data || []);
            setCompany(d.company || '');
            setError('');
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const showCreds = (d) => setCreds({
        company: d.company, id: d.device.id, name: d.device.device_name, token: d.kiosk_token,
    });

    const create = async () => {
        if (!newName.trim()) return;
        setBusy(true);
        try {
            const r = await authFetch('/api/qr/kiosk/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ device_name: newName.trim() }),
            });
            const d = await r.json().catch(() => ({}));
            if (!d.success) throw new Error(d.message || d.error || 'Kiosku s\'u krijua');
            setAddOpen(false);
            setNewName('');
            showCreds(d);
            load();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (dev) => {
        try {
            const r = await authFetch(`/api/qr/kiosk/${dev.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: !dev.is_active }),
            });
            const d = await r.json().catch(() => ({}));
            if (!d.success) throw new Error(d.error || 'Ndryshimi s\'u ruajt');
            setDevices((list) => list.map((x) => (x.id === dev.id ? d.device : x)));
        } catch (e) {
            setError(e.message);
        }
    };

    const rotate = async () => {
        const id = rotateId;
        setBusy(true);
        try {
            const r = await authFetch(`/api/qr/kiosk/${id}/rotate-token`, { method: 'POST' });
            const d = await r.json().catch(() => ({}));
            if (!d.success) throw new Error(d.error || 'Token-i s\'u ndërrua');
            setRotateId(null);
            showCreds(d);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const seen = (ts) => (ts ? new Date(ts).toLocaleString('sq-AL') : '—');

    /**
     * Kjo pajisje bëhet kioska — pa i shkruar tre fushat me dorë.
     *
     * Admini hyn në tablet, krijon kioskun (ose i ndërron token-in) dhe e
     * shtyp këtë. Çelësat janë ata që lexon KioskSetup (`hr_kiosk_*`). Kjo
     * është e vetmja rrugë që e hap kioskun vetë: #/kiosk s'aktivizon më asgjë
     * thjesht pse u hap me një sesion admini (krijonte kioskë të rinj pa fund).
     */
    const openHere = (c) => {
        try {
            localStorage.setItem('hr_kiosk_token', c.token);
            localStorage.setItem('hr_kiosk_device_id', String(c.id));
            localStorage.setItem('hr_kiosk_device_name', c.name || 'Kiosk');
            if (c.company) localStorage.setItem('hr_kiosk_tenant', c.company);
            else localStorage.removeItem('hr_kiosk_tenant');
        } catch (e) {
            setError('Shfletuesi s\'e lejon ruajtjen — fute token-in me dorë te #/kiosk.');
            return;
        }
        window.location.assign(`${window.location.origin}/#/kiosk`);
        window.location.reload();
    };

    return (
        <Paper variant="outlined" sx={{ p:2.5, borderRadius:2 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5, flexWrap:'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex:1 }}>
                    Pajisjet kiosk{company ? <> · <Box component="span" sx={{ fontFamily:'monospace', fontWeight:500 }}>{company}</Box></> : null}
                </Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setAddOpen(true)} sx={{ textTransform:'none', fontWeight:600, borderRadius:1.5 }}>
                    Shto kiosk
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb:1.5 }} onClose={() => setError('')}>{error}</Alert>}

            {loading ? (
                <Box sx={{ py:3, textAlign:'center' }}><CircularProgress size={22} /></Box>
            ) : devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py:1 }}>
                    Kjo kompani s'ka ende asnjë kiosk. Shto një dhe përdor kredencialet te tableti.
                </Typography>
            ) : (
                <Box sx={{ overflowX:'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Emri</TableCell>
                                <TableCell>Parë së fundi</TableCell>
                                <TableCell>Aktiv</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {devices.map((d) => (
                                <TableRow key={d.id}>
                                    <TableCell sx={{ fontFamily:'monospace' }}>{d.id}</TableCell>
                                    <TableCell>{d.device_name}</TableCell>
                                    <TableCell sx={{ whiteSpace:'nowrap' }}>{seen(d.last_seen_at)}</TableCell>
                                    <TableCell>
                                        <Switch size="small" checked={!!d.is_active} onChange={() => toggle(d)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Token i ri (i vjetri pushon së vlejturi)">
                                            <IconButton size="small" onClick={() => setRotateId(d.id)}>
                                                <LockResetIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {/* Kiosk i ri */}
            <Dialog open={addOpen} onClose={() => !busy && setAddOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Kiosk i ri</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth size="small" sx={{ mt:1 }}
                        label="Emri i pajisjes" placeholder='p.sh. "Kiosk Recepsioni"'
                        value={newName} onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)} disabled={busy}>Anulo</Button>
                    <Button variant="contained" onClick={create} disabled={busy || !newName.trim()}>
                        {busy ? <CircularProgress size={16} /> : 'Krijo'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Konfirmo token-in e ri */}
            <Dialog open={rotateId !== null} onClose={() => !busy && setRotateId(null)} maxWidth="xs">
                <DialogTitle>Token i ri për kioskun {rotateId}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Token-i aktual pushon së vlejturi menjëherë. Tableti duhet riaktivizuar me token-in e ri.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRotateId(null)} disabled={busy}>Anulo</Button>
                    <Button variant="contained" color="warning" onClick={rotate} disabled={busy}>
                        {busy ? <CircularProgress size={16} /> : 'Ndërro token-in'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Kredencialet — shfaqen një herë */}
            <Dialog open={!!creds} onClose={() => setCreds(null)} fullWidth maxWidth="sm">
                <DialogTitle>Kredencialet e kioskut «{creds?.name}»</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb:2 }}>
                        Token-i shfaqet vetëm tani. Fute te tableti (#/kiosk → Aktivizo Kioskin) para se ta mbyllësh këtë dritare —
                        ose, nëse je tashmë te tableti, shtyp «Hap kioskun në këtë pajisje».
                    </Alert>
                    {creds && <>
                        <CopyValue label="Kompania" value={creds.company} />
                        <CopyValue label="Device ID" value={creds.id} />
                        <CopyValue label="Kiosk Token" value={creds.token} />
                    </>}
                </DialogContent>
                <DialogActions sx={{ flexWrap:'wrap', gap:1 }}>
                    <Button variant="outlined" onClick={() => openHere(creds)} disabled={!creds?.token}>
                        Hap kioskun në këtë pajisje
                    </Button>
                    <Button variant="contained" onClick={() => setCreds(null)}>E ruajta</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

function KioskTab() {
    const [copied, setCopied] = React.useState(false);
    const kioskUrl = `${window.location.origin}/#/kiosk`;

    const copyUrl = () => {
        navigator.clipboard.writeText(kioskUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const browser = /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios'
                  : /Chrome/.test(navigator.userAgent) ? 'chrome' : 'other';

    return (
        <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display:'flex', alignItems:'center', gap:1 }}>
                <TabletIcon color="primary" /> Kiosk QR — Setup për Tablet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb:3 }}>
                Kiosku shfaq QR-kodin rrotullues në tablet. Punëtorët e skanojnë me telefonin e tyre për të regjistruar prezencën.
            </Typography>

            <Grid container spacing={3}>
                {/* URL Card */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p:2.5, borderRadius:2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            1. Hap URL-në e Kioskut në tablet
                        </Typography>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, bgcolor:'grey.50', borderRadius:1.5, p:1.5, mb:2 }}>
                            <Typography variant="body2" sx={{ flex:1, fontFamily:'monospace', wordBreak:'break-all', fontSize:'0.8rem' }}>
                                {kioskUrl}
                            </Typography>
                            <Tooltip title={copied ? 'U kopjua!' : 'Kopjo URL-në'}>
                                <IconButton size="small" onClick={copyUrl} color={copied ? 'success' : 'default'}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {/* Në të njëjtën dritare, jo në skedë të re: skeda e re s'e merr
                            sesionin (sessionStorage), dhe pa të kioska s'aktivizohet vetë. */}
                        <Button
                            variant="contained" fullWidth startIcon={<OpenInNewIcon />}
                            href={kioskUrl}
                            sx={{ borderRadius:1.5, textTransform:'none', fontWeight:600 }}
                        >
                            Hap kioskun në këtë pajisje
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:1 }}>
                            Kioska aktivizohet vetë për këtë kompani — pa Device ID dhe token.
                        </Typography>
                    </Paper>
                </Grid>

                {/* Install instructions */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p:2.5, borderRadius:2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            2. Instalo si PWA në tablet
                        </Typography>
                        <Stack spacing={1.5}>
                            <Alert severity="info" sx={{ fontSize:'0.78rem', py:0.5 }}>
                                <b>Android (Chrome):</b> Pasi të hapësh URL-në, shtyp menunë ⋮ lart djathtas → <b>"Add to Home Screen"</b> (Instalo aplikacionin)
                            </Alert>
                            <Alert severity="info" sx={{ fontSize:'0.78rem', py:0.5 }}>
                                <b>iPad (Safari):</b> Hap URL-në me Safari → kliko <b>Share ↑</b> → <b>"Add to Home Screen"</b> → <b>Add</b>
                            </Alert>
                            <Alert severity="success" sx={{ fontSize:'0.78rem', py:0.5 }}>
                                Pas instalimit, hap app-in nga Home Screen — tableti do të funksionojë si kiosk i pavarur.
                            </Alert>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Token info */}
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p:2.5, borderRadius:2, bgcolor:'grey.50' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            3. Aktivizo kioskin (herën e parë)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Te tableti, hyr si admin ose HR dhe shtyp <b>«Hap kioskun në këtë pajisje»</b> — kioska aktivizohet
                            vetë për këtë kompani dhe fillon të shfaqë QR-kodin. Në një tablet pa hyrje, te kioska shkruaj
                            email-in dhe fjalëkalimin e adminit/HR-së («Hyr dhe aktivizo kioskën»).
                        </Typography>
                    </Paper>
                </Grid>

                {/* Pajisjet e kësaj kompanie */}
                <Grid item xs={12}>
                    <KioskDevicesCard />
                </Grid>
            </Grid>
        </Box>
    );
}

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());

// ─── Modulet Tab ──────────────────────────────────────────────────────────────
function ModuletTab() {
    const { modules, setModule } = useModuleSettings();
    const moduleList = [
        {
            key: 'timeTracking',
            label: 'Time Tracking',
            description: 'Regjistrim i kohës së punës, timer live, raporte dhe eksport CSV.',
            icon: <TimerIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
        },
    ];
    return (
        <Box sx={{ maxWidth: 640, mx: 'auto', py: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Modulet shtesë</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Aktivizo ose çaktivizo funksionalitete shtesë. Ndryshimet hyjnë menjëherë në fuqi.
            </Typography>
            {moduleList.map(m => (
                <Paper key={m.key} variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    {m.icon}
                    <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={600}>{m.label}</Typography>
                        <Typography variant="body2" color="text.secondary">{m.description}</Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!modules[m.key]}
                                onChange={e => setModule(m.key, e.target.checked)}
                                color="primary"
                            />
                        }
                        label={modules[m.key] ? 'Aktiv' : 'Joaktiv'}
                        labelPlacement="start"
                    />
                </Paper>
            ))}
        </Box>
    );
}


// ─── Tab: Shabllonet ─────────────────────────────────────────────────────────
// Tre llojet e shablloneve rrinë pas një zgjedhësi segmentesh, jo pas një shiriti
// të dytë tabesh: dy shirita tabesh 44px larg njëri-tjetrit nuk lexohen dot se
// cili e komandon cilin. Panelet vijnë nga `templateKinds.js` — të njëjtat që
// përdor faqja /templates, jo kopje e dytë e tyre.
function ShablloneTab() {
    const [kind, setKind] = useState(TEMPLATE_KINDS[0].key);
    const { Panel } = TEMPLATE_KINDS.find(k => k.key === kind) || TEMPLATE_KINDS[0];

    // Pa `Alert` shpjegues këtu: secili panel e mban shpjegimin e vet te shiriti
    // i veglave, i saktë për llojin. Një Alert i përgjithshëm sipër tij thoshte
    // të njëjtën gjë me fjalë më të zbehta dhe hante 56px.
    return (
        <Box>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={kind}
                onChange={(_, v) => v && setKind(v)}
                aria-label="Lloji i shabllonit"
                sx={{
                    mb: 1.5,
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                        height: 30, px: 1.5, gap: 0.75,
                        textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                        borderColor: 'divider',
                    },
                }}
            >
                {TEMPLATE_KINDS.map(k => (
                    <ToggleButton key={k.key} value={k.key}>
                        {k.icon}{k.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Panel />
        </Box>
    );
}

const ALL_TABS = [
    // Renditja ndjek punën, jo historinë: kompania → rregullat e punës e pagës →
    // dokumentet e njoftimet. Skedat e reja u futën te grupi ku bëjnë pjesë,
    // jo në fund. Lidhjet përdorin çelësin (`?tab=llojet-punes`), jo numrin.
    { key:'profili', label:'Profili',        icon:<BusinessIcon       sx={{ fontSize:16 }} /> },
    { key:'filialet', label:'Filialet',      icon:<StorefrontIcon      sx={{ fontSize:16 }} /> },
    { key:'departamentet', label:'Departamentet', icon:<ApartmentIcon       sx={{ fontSize:16 }} /> },
    { key:'fiskal', label:'Fiskal',        icon:<AccountBalanceIcon  sx={{ fontSize:16 }} /> },
    { key:'rregullat-orarit', label:'Rregullat e orarit', icon:<ScheduleIcon sx={{ fontSize:16 }} /> },
    { key:'llojet-punes', label:'Llojet e punës',  icon:<ExtensionIcon sx={{ fontSize:16 }} /> },
    { key:'llojet-pushimeve', label:'Llojet e pushimeve', icon:<BeachAccessIcon sx={{ fontSize:16 }} /> },
    { key:'nderprerja', label:'Ndërprerja',        icon:<BlockIcon sx={{ fontSize:16 }} /> },
    { key:'dokumentet', label:'Dokumentet',   icon:<FolderOpenIcon       sx={{ fontSize:16 }} /> },
    { key:'shabllonet', label:'Shabllonet',   icon:<ArticleIcon         sx={{ fontSize:16 }} /> },
    { key:'kiosk-qr', label:'Kiosk QR',            icon:<QrCodeScannerIcon  sx={{ fontSize:16 }} /> },
    { key:'njoftime', label:'Njoftime',            icon:<NotificationsActiveIcon sx={{ fontSize:16, color:'#7c3aed' }} /> },
];

/** Emrat e vjetër të tabeve (lidhje të ruajtura, `/work-types`) → çelësat e sotëm. */
const TAB_ALIASES = {
    'work-types': 'llojet-punes',
    'llojet-e-punes': 'llojet-punes',
    'working-types': 'llojet-punes',
    'orari-rregullat': 'rregullat-orarit',
};


// ─── Main Component ───────────────────────────────────────────────────────────
export default function Company() {
    const [snack, setSnack]           = useState('');
    const [snackType, setSnackType]   = useState('success');
    const [manualInviteLink, setManualInviteLink] = useState('');
    const [manualInviteMessage, setManualInviteMessage] = useState('');
    const [users, setUsers]           = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSavingId, setUserSavingId] = useState(null);
    const [creatingUser, setCreatingUser] = useState(false);
    const [newUser, setNewUser]       = useState(BLANK_USER);
    const location = useLocation();
    // `?tab=` pranon fjalën përpara numrit. Indekset u treguan të thyeshme:
    // shtimi i një tabi i zhvendos të gjitha, dhe dy lidhje "Konfigurimi" tashmë
    // tregonin te numra që nuk ekzistonin më — faqja hapej me trupin bosh.
    // Lidhjet e vjetra drejt shabllonesh (kur ishin tabe këtu) çonin te faqja e
    // Konfigurimit pa asnjë tab përkatës — pra te "Profili", pa asnjë shenjë se
    // ku shkuan. Ridrejtoji te moduli i tyre.
    const MOVED_TO_TEMPLATES = {
        'shabllone-kontratash': 'kontratat',
        'shabllone-vendimesh':  'vendimet',
        'shabllone-verejtjesh': 'verejtjet',
    };
    // Njësoj për orarin dhe festat: nuk janë më tabe këtu, kanë modulin e vet.
    const MOVED_TO_MODULE = {
        'orari':    '/oret',
        'pushimet': '/leave-calendar',
    };
    const tabParam = (new URLSearchParams(location.search).get('tab') || '').trim().toLowerCase();
    const movedTab   = MOVED_TO_TEMPLATES[tabParam];
    const movedRoute = MOVED_TO_MODULE[tabParam];

    const [activeTab, setActiveTab]   = useState(() => {
        const raw = new URLSearchParams(location.search).get('tab') || '';
        const slug = raw.trim().toLowerCase();
        const bySlug = ALL_TABS.findIndex(t => t.key === (TAB_ALIASES[slug] || slug));
        if (bySlug >= 0) return bySlug;
        const n = parseInt(raw, 10);
        return Number.isInteger(n) && n >= 0 && n < ALL_TABS.length ? n : 0;
    });
    const tabKey = ALL_TABS[activeTab]?.key;
    const [companyInfo, setCompanyInfo] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState({ open: false, user: null });
    const [detailsSaving, setDetailsSaving] = useState(false);
    const [sendingAccessLink, setSendingAccessLink] = useState(false);
    const [addUserDialog, setAddUserDialog] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [customRoles, setCustomRoles] = useState([]);
    const [customRolesLoading, setCustomRolesLoading] = useState(false);

    const [emailChangeDialog, setEmailChangeDialog] = useState({ open: false, user: null });
    const [emailChangeData, setEmailChangeData] = useState({ newEmail: '', confirmEmail: '' });
    const [emailChangeSaving, setEmailChangeSaving] = useState(false);
    const [emailCheckResult, setEmailCheckResult] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid' | 'same'

    const showSnack = (msg, type = 'success') => { setSnack(msg); setSnackType(type); };

    const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm({
        defaultValues: { ...EMAIL_DEFAULTS, BrandColor: BRAND_COLOR_DEFAULT, CompanyActive: true },
    });
    const brandColor   = watch('BrandColor') || BRAND_COLOR_DEFAULT;
    const companyActive = boolFromValue(watch('CompanyActive'));
    const logoSrc      = watch('Logo');

    const setImage = (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => setValue('Logo', 'data:image/png;base64,' + reader.result.split(',')[1]);
        reader.readAsDataURL(file);
    };

    const loadUsers = useCallback(() => {
        setUsersLoading(true);
        apiService().get('users').then(r => {
            if (r?.success) setUsers((r.data || []).map(normalizeUser));
        }).catch(() => {
            setUsers([]);
        }).finally(() => setUsersLoading(false));
    }, []);

    useEffect(() => {
        apiService().get('settings').then(r => {
            const raw = r?.data?.[0] || {};
            // CompanyActive default true nëse nuk është vendosur shprehimisht
            const companyActiveVal = (raw.CompanyActive !== undefined && raw.CompanyActive !== null)
                ? boolFromValue(raw.CompanyActive)
                : true;
            const payload = { ...EMAIL_DEFAULTS, ...raw, BrandColor: raw.BrandColor || BRAND_COLOR_DEFAULT, CompanyActive: companyActiveVal };
            Object.keys(payload).forEach(f => setValue(f, payload[f]));
            setCompanyInfo(raw || null);
        });
    }, [setValue]);

    useEffect(() => { loadUsers(); }, [loadUsers]);

    const loadCustomRoles = useCallback(() => {
        setCustomRolesLoading(true);
        apiService().get('customRoles').then(r => {
            if (r?.success) setCustomRoles(r.data || []);
        }).catch(() => {}).finally(() => setCustomRolesLoading(false));
    }, []);
    useEffect(() => { loadCustomRoles(); }, [loadCustomRoles]);

    const onSubmit = (data) => {
        const id = companyInfo?.Id || companyInfo?.id;
        // Pa çelësat SMTP: serveri e lexon mungesën si «mos e prek», ndaj një
        // kompani që e ka konfiguruar vetë postën nuk e humb me këtë ruajtje.
        const payload = { ...data, Id: id, Logo: data.Logo || '', CompanyActive: boolFromValue(data.CompanyActive, true) };
        for (const k of ['SMTP_HOST', 'SMTP_PORT', 'SMTP_SECURE', 'SMTP_USER', 'SMTP_PASS', 'SMTP_FROM', 'SMTP_REPLY_TO']) delete payload[k];
        const req = id ? apiService().put('settings', payload) : apiService().post('settings', payload);
        req.then(r => {
            if (r?.success) showSnack('Kompania u ruajt me sukses!');
            else            showSnack('Gabim gjatë ruajtjes.', 'error');
        }).catch(() => showSnack('Gabim gjatë ruajtjes.', 'error'));
    };

    const handleRoleChange = (id, role) => setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));

    const buildPayload = (u) => ({
        Id: u.id, UserName: u.userName, Email: u.email,
        Name: u.firstName, Surname: u.lastName,
        Role: normalizeRoleValue(u.role),
        Phone: u.phone, JobTitle: u.jobTitle, Notes: u.notes,
        Active: u.active !== false,
        Permissions: u.permissions ? JSON.stringify(u.permissions) : null,
    });

    const putUser = (userId, payload) =>
        authFetch(`/api/users/${userId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }).then(r => r.json());

    const saveUserRole = (user) => {
        if (!user.id) { showSnack('Gabim: ID mungon.', 'error'); return; }
        setUserSavingId(user.id);
        putUser(user.id, buildPayload(user)).then(r => {
            if (r?.success) { setUsers(prev => prev.map(u => u.id === user.id ? { ...u, originalRole: user.role } : u)); showSnack('Roli u përditësua.'); }
            else showSnack('Gabim gjatë ruajtjes.', 'error');
        }).catch(() => showSnack('Gabim.', 'error')).finally(() => setUserSavingId(null));
    };

    const openDetails  = (user) => {
        const perms = user.permissions || ROLE_DEFAULT_PERMS[user.role] || ROLE_DEFAULT_PERMS.employee;
        setDetailsDialog({ open: true, user: { ...user, permissions: { ...perms } } });
    };
    const closeDetails = () => setDetailsDialog({ open: false, user: null });

    const toggleUserActive = async (user) => {
        const newActive = user.active === false ? true : false;
        setUserSavingId(user.id);
        try {
            const r = await putUser(user.id, { ...buildPayload(user), Active: newActive });
            if (r?.success) {
                setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: newActive } : u));
                showSnack(newActive ? 'Përdoruesi u aktivizua.' : 'Llogaria u çaktivizua.');
            } else showSnack('Gabim gjatë ndryshimit të statusit.', 'error');
        } catch { showSnack('Gabim.', 'error'); }
        finally { setUserSavingId(null); }
    };

    const saveDetails = () => {
        if (!detailsDialog.user) return;
        const uid = detailsDialog.user.id;
        if (!uid) { showSnack('Gabim: ID e përdoruesit mungon.', 'error'); return; }
        setDetailsSaving(true);
        putUser(uid, buildPayload(detailsDialog.user)).then(r => {
            if (r?.success) { setUsers(prev => prev.map(u => u.id === uid ? { ...u, ...detailsDialog.user } : u)); showSnack('Të dhënat u ruajtën.'); closeDetails(); }
            else showSnack('Gabim.', 'error');
        }).catch(() => showSnack('Gabim.', 'error')).finally(() => setDetailsSaving(false));
    };

    const sendAccessLink = async () => {
        if (!detailsDialog.user?.id) return;
        setSendingAccessLink(true);
        try {
            const result = await sendInviteEmail(detailsDialog.user.id, { now: true });
            showInviteResult(result);
        } catch (e) {
            showSnack(e?.message || 'Dërgimi i ftesës dështoi.', 'error');
        } finally {
            setSendingAccessLink(false);
        }
    };

    const openEmailChange = (user) => {
        setEmailChangeDialog({ open: true, user });
        setEmailChangeData({ newEmail: '', confirmEmail: '' });
        setEmailCheckResult(null);
    };
    const closeEmailChange = () => {
        setEmailChangeDialog({ open: false, user: null });
        setEmailChangeData({ newEmail: '', confirmEmail: '' });
        setEmailCheckResult(null);
    };

    const checkEmailAvailability = async (email, userId) => {
        if (!isValidEmail(email)) { setEmailCheckResult('invalid'); return; }
        if (email.toLowerCase().trim() === emailChangeDialog.user?.email?.toLowerCase()) {
            setEmailCheckResult('same'); return;
        }
        setEmailCheckResult('checking');
        try {
            const r = await authFetch(
                `/api/users/check-email?email=${encodeURIComponent(email)}&excludeId=${userId}`
            ).then(x => x.json());
            setEmailCheckResult(r.available ? 'available' : 'taken');
        } catch {
            setEmailCheckResult(null);
        }
    };

    const saveEmailChange = async () => {
        const { user } = emailChangeDialog;
        const { newEmail, confirmEmail } = emailChangeData;
        if (!isValidEmail(newEmail)) { showSnack('Formati i emailit është i pavlefshëm.', 'error'); return; }
        if (newEmail.toLowerCase().trim() !== confirmEmail.toLowerCase().trim()) {
            showSnack('Email-et nuk përputhen.', 'error'); return;
        }
        if (newEmail.toLowerCase().trim() === user.email?.toLowerCase()) {
            showSnack('Emaili i ri duhet të jetë i ndryshëm nga ai aktual.', 'error'); return;
        }
        setEmailChangeSaving(true);
        try {
            const r = await authFetch(`/api/users/${user.id}/change-email`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newEmail }),
            }).then(x => x.json());
            if (r.success) {
                setUsers(prev => prev.map(u => u.id === user.id ? { ...u, email: r.email } : u));
                if (detailsDialog.open && detailsDialog.user?.id === user.id) {
                    setDetailsDialog(p => ({ ...p, user: { ...p.user, email: r.email } }));
                }
                showSnack('Emaili u ndryshua me sukses.');
                closeEmailChange();
            } else {
                showSnack(r.error || 'Gabim gjatë ndryshimit të emailit.', 'error');
            }
        } catch {
            showSnack('Gabim i papritur. Provo sërish.', 'error');
        } finally {
            setEmailChangeSaving(false);
        }
    };

    // Dërgo ftes̈ë me link token-based (backend gjeneron token + dërgon email)
    const sendInviteEmail = async (userId, { now = false } = {}) => {
        const r = await authFetch(`/api/users/${userId}/send-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ now }),
        });
        const data = await r.json();
        if (!data?.success) throw new Error(data?.error || 'Dërgimi i ftesës dështoi');
        return data; // { scheduled?, message, emailSent, setLink? }
    };

    const showInviteResult = (result, prefix = '') => {
        if (result?.scheduled) {
            showSnack(`${prefix}${result.message || 'Ftesa u planifikua.'}`, 'info');
        } else if (result?.emailSent === false) {
            if (result.setLink) {
                setManualInviteMessage(result.message || 'Email-i nuk u dërgua.');
                setManualInviteLink(result.setLink);
            }
            showSnack(`${prefix}${result.message || 'Email-i nuk u dërgua.'}`, 'warning');
        } else {
            showSnack(`${prefix}${result?.message || 'Ftesa u dërgua me sukses.'}`, 'success');
        }
    };

    const handleCreateUser = () => {
        if (!newUser.firstName?.trim() || !newUser.lastName?.trim() || !newUser.email?.trim()) {
            showSnack('Emri, mbiemri dhe emaili janë të detyrueshëm.', 'error'); return;
        }
        const prepared = { ...newUser, userName: newUser.userName?.trim() || newUser.email.split('@')[0] };
        setCreatingUser(true);
        apiService().post('users', buildPayload(prepared)).then(async r => {
            if (r?.success) {
                const createdId = r?.data?.Id || r?.data?.id;
                const created   = { ...prepared, id: createdId };
                if (newUser.sendInvite && createdId) {
                    try {
                        const ir = await sendInviteEmail(createdId);
                        showInviteResult(ir, 'Përdoruesi u shtua. ');
                    } catch (e) {
                        showSnack(`Përdoruesi u shtua, por ftesa dështoi: ${e?.message || 'gabim'}.`, 'warning');
                    }
                } else {
                    showSnack('Përdoruesi u shtua me sukses.');
                }
                setNewUser(BLANK_USER);
                setAddUserDialog(false);
                loadUsers();
                openDetails(created);
            } else showSnack('Gabim gjatë shtimit.', 'error');
        }).catch(() => showSnack('Gabim.', 'error')).finally(() => setCreatingUser(false));
    };

    const initials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

    const filteredUsers = userSearch
        ? users.filter(u => `${u.firstName} ${u.lastName} ${u.email} ${u.userName}`.toLowerCase().includes(userSearch.toLowerCase()))
        : users;

    if (movedTab) return <Redirect to={`/templates?tab=${movedTab}`} />;
    if (movedRoute) return <Redirect to={movedRoute} />;

    return (
        <Fragment>
        <Box>
            <PageHeader
                title="Kompania & Konfigurim"
                subtitle="Profili, emaili, branding, përdoruesit dhe konfigurimet e sistemit"
                actions={
                    tabKey === 'profili' ? (
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit(onSubmit)} sx={{ borderRadius:1.5, textTransform:'none' }}>
                            Ruaj ndryshimet
                        </Button>
                    ) : null
                }
            />

            <Paper sx={{ borderRadius:2, border:'1px solid', borderColor:'divider', overflow:'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid', borderColor: 'divider',
                        '& .MuiTab-root': { minHeight:44, textTransform:'none', fontWeight:600, fontSize:'0.85rem', gap:0.75 },
                        '& .Mui-selected': { color:'primary.main' },
                    }}
                >
                    {ALL_TABS.map((t, i) => (
                        <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
                    ))}
                </Tabs>

                <Box sx={{ p: { xs:2, md:3 } }}>

                    {/* ── Profili i kompanisë ── */}
                    {tabKey === 'profili' && (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={3}>
                                {/* Logo & brand */}
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:1.5, p:2, bgcolor:'grey.50', borderRadius:2, border:'1px dashed', borderColor:'divider' }}>
                                        <Box sx={{ position:'relative' }}>
                                            {logoSrc ? (
                                                <Box component="img" src={logoSrc} alt="logo" sx={{ width:96, height:96, borderRadius:2, objectFit:'cover', border:'2px solid', borderColor:'divider' }} />
                                            ) : (
                                                <Avatar sx={{ width:96, height:96, bgcolor:brandColor, fontSize:28, borderRadius:2 }}>
                                                    {initials(watch('CompanyName') || 'C')}
                                                </Avatar>
                                            )}
                                            <label htmlFor="logoUpload">
                                                <input type="file" id="logoUpload" style={{ display:'none' }} accept="image/*" onChange={setImage} />
                                                <IconButton size="small" component="span" sx={{ position:'absolute', bottom:-6, right:-6, bgcolor:'background.paper', border:'1px solid', borderColor:'divider', boxShadow:1 }}>
                                                    <CameraAltIcon sx={{ fontSize:14 }} />
                                                </IconButton>
                                            </label>
                                        </Box>
                                        <FormControlLabel sx={{ m:0 }} control={
                                            <Switch size="small" checked={companyActive} onChange={(_, v) => setValue('CompanyActive', v)} />
                                        } label={<Typography variant="caption" fontWeight={600}>{companyActive ? 'Aktive' : 'Joaktive'}</Typography>} />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={9}>
                                    <SectionBox title="Informacioni bazë" icon={<BusinessIcon sx={{ fontSize:16, color:'primary.main' }} />}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12} sm={6}><FieldLabel>Emri i kompanisë</FieldLabel><TextField size="small" fullWidth error={!!errors.CompanyName} {...register('CompanyName')} /></Grid>
                                            <Grid item xs={12} sm={3}><FieldLabel>NUI / NF</FieldLabel><TextField size="small" fullWidth {...register('NUI')} /></Grid>
                                            <Grid item xs={12} sm={3}><FieldLabel>Numri fiskal</FieldLabel><TextField size="small" fullWidth {...register('FiscalNumber')} /></Grid>
                                            <Grid item xs={12} sm={4}><FieldLabel>Email zyrtar</FieldLabel><TextField size="small" fullWidth type="email" {...register('Email')} /></Grid>
                                            <Grid item xs={12} sm={4}><FieldLabel>Telefoni</FieldLabel><TextField size="small" fullWidth {...register('Phone')} /></Grid>
                                            <Grid item xs={12} sm={4}><FieldLabel>Faqja web</FieldLabel><TextField size="small" fullWidth {...register('Website')} /></Grid>
                                            <Grid item xs={12} sm={6}><FieldLabel>Adresa</FieldLabel><TextField size="small" fullWidth {...register('Address')} /></Grid>
                                            <Grid item xs={12} sm={3}><FieldLabel>Qyteti</FieldLabel><TextField size="small" fullWidth {...register('City')} /></Grid>
                                            <Grid item xs={12} sm={3}><FieldLabel>Shteti</FieldLabel><TextField size="small" fullWidth {...register('Country')} /></Grid>
                                            <Grid item xs={12} sm={6}><FieldLabel>Përfaqësuesi / Drejtori</FieldLabel><TextField size="small" fullWidth placeholder="p.sh. Fisnik Sejdiu, Drejtor" {...register('Director')} /></Grid>
                                        </Grid>
                                    </SectionBox>
                                    <Divider sx={{ my:2 }} />
                                    <SectionBox title="Llogaria bankare" icon={<BusinessIcon sx={{ fontSize:16, color:'success.main' }} />}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12} sm={6}><FieldLabel>Emri i bankës</FieldLabel><TextField size="small" fullWidth {...register('BankName')} /></Grid>
                                            <Grid item xs={12} sm={6}><FieldLabel>Numri i llogarisë (IBAN)</FieldLabel><TextField size="small" fullWidth {...register('BankAccount')} /></Grid>
                                        </Grid>
                                    </SectionBox>
                                    <Divider sx={{ my:2 }} />
                                    {/* Kredencialet SMTP u hoqën nga ky ekran: postën e dërgon Nexuma me
                                        llogarinë e vet, dhe një server poste i shkruar gabim këtu i ndalonte
                                        në heshtje të gjitha njoftimet e kompanisë. Mbeten dy adresat që
                                        vërtet i takojnë kompanisë — ku shkojnë njoftimet e prezencës. */}
                                    <SectionBox title="Njoftimet e prezencës" icon={<EmailIcon sx={{ fontSize:16, color:'info.main' }} />}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12} sm={6}><FieldLabel>Email monitorues prezence</FieldLabel><TextField size="small" fullWidth type="email" placeholder="monitoring@company.com" {...register('MONITORING_EMAIL')} /></Grid>
                                            <Grid item xs={12} sm={6}><FieldLabel>Email arkivim prezence</FieldLabel><TextField size="small" fullWidth type="email" placeholder="archive@company.com" {...register('ARCHIVE_EMAIL')} /></Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Aty shkojnë përmbledhjet e mungesave dhe arkivi i prezencës. Të gjithë emailat e sistemit — ftesat, njoftimet, vendimet — i dërgon Nexuma nga noreply@nexuma.app.
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </SectionBox>
                                    <Divider sx={{ my:2 }} />
                                    <SectionBox title="Single Sign-On (SSO) — Google Workspace" icon={<BusinessIcon sx={{ fontSize:16, color:'primary.main' }} />}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12}>
                                                <FormControlLabel sx={{ m:0 }}
                                                    control={<Switch size="small" checked={boolFromValue(watch('GoogleSsoEnabled'))} onChange={(_, v) => setValue('GoogleSsoEnabled', v)} />}
                                                    label={<Typography variant="body2" sx={{ fontWeight:600 }}>Aktivizo Hyrjen me Google (SSO)</Typography>}
                                                />
                                                <Typography variant="caption" sx={{ color:'text.secondary', display:'block' }}>
                                                    Kur është aktiv <b>dhe</b> Client ID është i plotësuar, butoni “Hyr me Google” shfaqet në faqen e kyçjes. Fike për ta çaktivizuar pa i fshirë kredencialet.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FieldLabel>Google Client ID</FieldLabel>
                                                <TextField size="small" fullWidth placeholder="xxxx.apps.googleusercontent.com" {...register('GoogleClientId')} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FieldLabel>Google Client Secret</FieldLabel>
                                                <TextField
                                                    size="small" fullWidth type="password"
                                                    placeholder={watch('GoogleSecretSet') ? '•••••••• (i ruajtur — shkruaj për ta ndryshuar)' : 'Client Secret nga Google Cloud'}
                                                    {...register('GoogleClientSecret')}
                                                />
                                                <Typography variant="caption" sx={{ color:'text.secondary', mt:0.5, display:'block' }}>
                                                    Ruhet i sigurt dhe <b>nuk shfaqet më pas ruajtjes</b>. Lëre bosh për ta mbajtur të njëjtin.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FieldLabel>Domeni i Google Workspace</FieldLabel>
                                                <TextField size="small" fullWidth placeholder="p.sh. ata-bk.com  (disa domaine: ndaji me presje)" {...register('GoogleWorkspaceDomain')} />
                                                <Typography variant="caption" sx={{ color:'text.secondary', mt:0.5, display:'block' }}>
                                                    Punëtorët me email Google të këtij domeni mund të hyjnë me “Hyr me Google” — vetëm nëse kanë tashmë llogari në Nexuma. Lëre bosh për të lejuar çdo domen (porta mbetet: llogaria duhet të ekzistojë në Nexuma).
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </SectionBox>
                                    <Divider sx={{ my:2 }} />
                                    <SectionBox title="Single Sign-On (SSO) — Microsoft / Outlook" icon={<BusinessIcon sx={{ fontSize:16, color:'primary.main' }} />}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12}>
                                                <FormControlLabel sx={{ m:0 }}
                                                    control={<Switch size="small" checked={boolFromValue(watch('MicrosoftSsoEnabled'))} onChange={(_, v) => setValue('MicrosoftSsoEnabled', v)} />}
                                                    label={<Typography variant="body2" sx={{ fontWeight:600 }}>Aktivizo Hyrjen me Microsoft (SSO)</Typography>}
                                                />
                                                <Typography variant="caption" sx={{ color:'text.secondary', display:'block' }}>
                                                    Kur është aktiv <b>dhe</b> Client ID është i plotësuar, butoni “Hyr me Microsoft” shfaqet në faqen e kyçjes. Fike për ta çaktivizuar pa i fshirë kredencialet.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FieldLabel>Application (client) ID</FieldLabel>
                                                <TextField size="small" fullWidth placeholder="p.sh. 11111111-2222-3333-4444-555555555555" {...register('MicrosoftClientId')} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FieldLabel>Client Secret</FieldLabel>
                                                <TextField
                                                    size="small" fullWidth type="password"
                                                    placeholder={watch('MicrosoftSecretSet') ? '•••••••• (i ruajtur — shkruaj për ta ndryshuar)' : 'Client Secret nga Azure / Entra ID'}
                                                    {...register('MicrosoftClientSecret')}
                                                />
                                                <Typography variant="caption" sx={{ color:'text.secondary', mt:0.5, display:'block' }}>
                                                    Ruhet i sigurt dhe <b>nuk shfaqet më pas ruajtjes</b>. Lëre bosh për ta mbajtur të njëjtin.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FieldLabel>Directory (tenant) ID</FieldLabel>
                                                <TextField size="small" fullWidth placeholder="common · organizations · ose GUID i tenant-it" {...register('MicrosoftTenant')} />
                                                <Typography variant="caption" sx={{ color:'text.secondary', mt:0.5, display:'block' }}>
                                                    Lëre bosh ose “common” për të lejuar llogari pune dhe personale. “organizations” = vetëm llogari pune/shkolle.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FieldLabel>Domeni i lejuar</FieldLabel>
                                                <TextField size="small" fullWidth placeholder="p.sh. kompania.com  (disa domaine: ndaji me presje)" {...register('MicrosoftWorkspaceDomain')} />
                                                <Typography variant="caption" sx={{ color:'text.secondary', mt:0.5, display:'block' }}>
                                                    Lëre bosh për të lejuar çdo domen (porta mbetet: llogaria duhet të ekzistojë në Nexuma).
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ color:'text.secondary', display:'block' }}>
                                                    Redirect URI për Azure: <b>{`${(process.env.REACT_APP_API_URL||'')}/api/auth/microsoft/callback`}</b>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </SectionBox>
                                </Grid>
                            </Grid>
                        </form>
                    )}

                    {tabKey === 'filialet'      && <BranchetTab />}
                    {tabKey === 'departamentet' && <DepartamentetTab />}
                    {tabKey === 'fiskal'        && <FiskalTab />}
                    {tabKey === 'dokumentet'    && <DokumentetTab />}
                    {tabKey === 'shabllonet'    && <ShablloneTab />}
                    {tabKey === 'kiosk-qr'      && <KioskTab />}
                    {tabKey === 'njoftime'      && <NotificationSettings />}
                    {tabKey === 'rregullat-orarit' && <RregullatOraritTab />}
                    {tabKey === 'llojet-pushimeve' && <LlojetPushimitTab />}
                    {tabKey === 'nderprerja'    && <NderprerjaTab />}
                    {tabKey === 'llojet-punes'  && <WorkingTypesTab />}

                </Box>
            </Paper>

            {/* ── Add User Dialog ── */}
            <Dialog
                open={addUserDialog}
                onClose={() => { setAddUserDialog(false); setNewUser(BLANK_USER); }}
                maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 2.5 } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                        <PersonAddAltIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Shto përdorues të ri</Typography>
                        <Typography variant="caption" color="text.secondary">Plotëso të dhënat dhe cakto rolin</Typography>
                    </Box>
                </Box>
                <Divider />
                <DialogContent sx={{ pt: 2.5 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Emri *" value={newUser.firstName}
                                onChange={e => setNewUser(p => ({ ...p, firstName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Mbiemri *" value={newUser.lastName}
                                onChange={e => setNewUser(p => ({ ...p, lastName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Adresa email *" type="email" value={newUser.email}
                                onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></InputAdornment> }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select size="small" fullWidth label="Roli & privilegjet" value={newUser.role}
                                onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                                {ROLES_RBAC.map(r => (
                                    <MenuItem key={r.id} value={r.id}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                                            </Box>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography variant="caption" fontWeight={700} sx={{ color: ROLES_RBAC.find(x => x.id === newUser.role)?.color || 'text.primary', display: 'block', mb: 0.25 }}>
                                    {ROLES_RBAC.find(x => x.id === newUser.role)?.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                    {ROLES_RBAC.find(x => x.id === newUser.role)?.desc}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: newUser.sendInvite ? 'primary.50' : 'transparent' }}>
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                        <Checkbox size="small" checked={newUser.sendInvite}
                                            onChange={e => setNewUser(p => ({ ...p, sendInvite: e.target.checked }))} />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>Dërgo email me kredencialet</Typography>
                                            <Typography variant="caption" color="text.secondary">Përdoruesi merr email me fjalëkalimin e përkohshëm</Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => { setAddUserDialog(false); setNewUser(BLANK_USER); }}
                        variant="outlined" sx={{ borderRadius: 1.5, textTransform: 'none', flex: 1 }}>
                        Anulo
                    </Button>
                    <Button onClick={handleCreateUser} variant="contained" disabled={creatingUser}
                        startIcon={creatingUser ? <CircularProgress size={14} color="inherit" /> : <PersonAddAltIcon />}
                        sx={{ borderRadius: 1.5, textTransform: 'none', flex: 2, fontWeight: 600 }}>
                        {creatingUser ? 'Duke shtuar...' : 'Shto përdoruesin'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit User Dialog ── */}
            <Dialog open={detailsDialog.open} onClose={closeDetails} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
                {detailsDialog.user && (() => {
                    const u = detailsDialog.user;
                    const setU = (patch) => setDetailsDialog(p => ({ ...p, user: { ...p.user, ...patch } }));
                    const setPermission = (mod, val) => setDetailsDialog(p => ({
                        ...p, user: { ...p.user, permissions: { ...(p.user.permissions || {}), [mod]: val } }
                    }));
                    const applyRoleDefaults = () => {
                        const perms = ROLE_DEFAULT_PERMS[u.role] || ROLE_DEFAULT_PERMS.employee;
                        setU({ permissions: { ...perms } });
                    };
                    const onRoleChange = (e) => {
                        const newRole = e.target.value;
                        const perms = ROLE_DEFAULT_PERMS[newRole] || ROLE_DEFAULT_PERMS.employee;
                        setU({ role: newRole, permissions: { ...perms } });
                    };
                    const roleInfo = ROLES_RBAC.find(r => r.id === u.role);
                    return (
                        <>
                            {/* Dialog header */}
                            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Avatar
                                    sx={{
                                        width: 46, height: 46, fontSize: 16, fontWeight: 700,
                                        bgcolor: u.active === false ? 'grey.400' : (roleInfo?.color || '#1976d2'),
                                    }}
                                >
                                    {initials(`${u.firstName || ''} ${u.lastName || ''}`)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                        {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userName}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                                        <Tooltip title="Ndrysho emailin">
                                            <Chip
                                                size="small"
                                                label="Ndrysho emailin"
                                                icon={<AlternateEmailIcon sx={{ fontSize: '12px !important' }} />}
                                                clickable
                                                onClick={() => { closeDetails(); openEmailChange(u); }}
                                                sx={{
                                                    height: 20, fontSize: 10, fontWeight: 600,
                                                    bgcolor: '#EEF2FF', color: '#2563EB',
                                                    border: '1px solid #BFDBFE',
                                                    '& .MuiChip-icon': { color: '#2563EB', ml: 0.5 },
                                                    '&:hover': { bgcolor: '#DBEAFE' },
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {roleInfo && (
                                        <Chip size="small" label={roleInfo.name}
                                            sx={{ fontSize: 11, height: 22, bgcolor: roleInfo.color + '18', color: roleInfo.color, border: `1px solid ${roleInfo.color}40`, fontWeight: 600 }} />
                                    )}
                                    {(() => { const st = accountStatusChip(u); return (
                                    <Chip size="small"
                                        label={st.label}
                                        color={st.color}
                                        variant={st.variant}
                                        sx={{ fontSize: 11, height: 22 }}
                                    />); })()}
                                </Stack>
                            </Box>

                            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
                                <Grid container spacing={3}>

                                    {/* ── Left column: Basic info ── */}
                                    <Grid item xs={12} md={5}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary"
                                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                                            Informacioni bazë
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={6}>
                                                <TextField size="small" fullWidth label="Emri"
                                                    value={u.firstName || ''}
                                                    onChange={e => setU({ firstName: e.target.value })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField size="small" fullWidth label="Mbiemri"
                                                    value={u.lastName || ''}
                                                    onChange={e => setU({ lastName: e.target.value })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField size="small" fullWidth label="Telefoni"
                                                    value={u.phone || ''}
                                                    onChange={e => setU({ phone: e.target.value })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField size="small" fullWidth label="Pozita / Titulli"
                                                    value={u.jobTitle || ''}
                                                    onChange={e => setU({ jobTitle: e.target.value })} />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField size="small" fullWidth label="Shënime" multiline minRows={3}
                                                    value={u.notes || ''}
                                                    onChange={e => setU({ notes: e.target.value })} />
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 2 }} />

                                        {/* Active toggle */}
                                        <Box sx={{
                                            p: 1.5, borderRadius: 1.5, border: '1px solid',
                                            borderColor: u.active === false ? 'error.200' : 'success.200',
                                            bgcolor: u.active === false ? '#FFF5F5' : '#F0FDF4',
                                        }}>
                                            <FormControlLabel
                                                sx={{ m: 0, width: '100%' }}
                                                control={
                                                    <Switch size="small"
                                                        checked={u.active !== false}
                                                        onChange={e => setU({ active: e.target.checked })}
                                                        color={u.active === false ? 'default' : 'success'}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ ml: 0.5 }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {u.active === false ? 'Llogari joaktive' : 'Llogari aktive'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {u.active === false
                                                                ? 'Ky përdorues nuk mund të hyjë në sistem'
                                                                : accountStatus(u) === 'pending_activation'
                                                                    ? `Hyrja hapet më ${formatDMY(u.activationDate)} (data e aktivizimit te profili i punëtorit)`
                                                                    : 'Ka qasje sipas privilegjeve të caktuara'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </Box>
                                    </Grid>

                                    {/* ── Right column: Role + Privileges ── */}
                                    <Grid item xs={12} md={7}>
                                        {/* Role selector */}
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Roli bazë
                                            </Typography>
                                        </Stack>
                                        <TextField select size="small" fullWidth value={u.role || 'staff'}
                                            onChange={onRoleChange} sx={{ mb: 2 }}>
                                            {ROLES_RBAC.map(r => (
                                                <MenuItem key={r.id} value={r.id}>
                                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{r.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{r.desc}</Typography>
                                                        </Box>
                                                    </Stack>
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        {/* Permission editor header */}
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Privilegjet sipas modulit
                                            </Typography>
                                            <Tooltip title="Rivendos privilegjet bazuar në rolin e zgjedhur">
                                                <Button size="small" variant="text"
                                                    onClick={applyRoleDefaults}
                                                    sx={{ textTransform: 'none', fontSize: 11, color: 'text.secondary', py: 0, minWidth: 0 }}>
                                                    ↺ Rivendos nga roli
                                                </Button>
                                            </Tooltip>
                                        </Stack>

                                        {/* Apply custom role template */}
                                        {customRoles.length > 0 && (
                                            <Box sx={{ mb: 1.5, p: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                                    <WorkspacePremiumIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />
                                                    Zbato rol të personalizuar
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {customRoles.map(cr => (
                                                        <Tooltip key={cr.id} title={cr.description || cr.name} placement="top">
                                                            <Chip
                                                                size="small"
                                                                label={cr.name}
                                                                clickable
                                                                onClick={() => setDetailsDialog(p => ({ ...p, user: { ...p.user, permissions: { ...cr.permissions } } }))}
                                                                sx={{
                                                                    fontSize: 11, height: 24,
                                                                    bgcolor: (cr.color || '#7C3AED') + '15',
                                                                    color: cr.color || '#7C3AED',
                                                                    border: `1px solid ${(cr.color || '#7C3AED')}40`,
                                                                    fontWeight: 600,
                                                                    '&:hover': { bgcolor: (cr.color || '#7C3AED') + '30' },
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}

                                        <PermissionEditor
                                            permissions={u.permissions}
                                            onChange={setPermission}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>

                            <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                                <Button onClick={closeDetails} variant="outlined"
                                    sx={{ borderRadius: 1.5, textTransform: 'none' }}>
                                    Anulo
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                <Button
                                    onClick={sendAccessLink}
                                    variant="outlined"
                                    color="secondary"
                                    disabled={sendingAccessLink || detailsSaving}
                                    startIcon={sendingAccessLink ? <CircularProgress size={14} color="inherit" /> : <LockResetIcon />}
                                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                                >
                                    {sendingAccessLink ? 'Duke dërguar...' : 'Dërgo link aksesi'}
                                </Button>
                                <Button onClick={saveDetails} variant="contained" disabled={detailsSaving}
                                    startIcon={detailsSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>
                                    {detailsSaving ? 'Duke ruajtur...' : 'Ruaj ndryshimet'}
                                </Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            {/* ── Email Change Dialog ── */}
            <Dialog
                open={emailChangeDialog.open}
                onClose={emailChangeSaving ? undefined : closeEmailChange}
                maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
            >
                {emailChangeDialog.user && (() => {
                    const u = emailChangeDialog.user;
                    const { newEmail, confirmEmail } = emailChangeData;
                    const emailValid    = isValidEmail(newEmail);
                    const emailsSame    = newEmail.toLowerCase().trim() === u.email?.toLowerCase();
                    const emailsMatch   = newEmail.toLowerCase().trim() === confirmEmail.toLowerCase().trim() && confirmEmail.length > 0;
                    const canSubmit     = emailValid && !emailsSame && emailsMatch && emailCheckResult === 'available' && !emailChangeSaving;

                    const statusColor = {
                        available: '#16A34A', taken: '#DC2626', invalid: '#D97706',
                        same: '#D97706', checking: '#2563EB',
                    };
                    const statusIcon = {
                        available: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
                        taken:     <ErrorOutlineIcon       sx={{ fontSize: 16 }} />,
                        invalid:   <ErrorOutlineIcon       sx={{ fontSize: 16 }} />,
                        same:      <ErrorOutlineIcon       sx={{ fontSize: 16 }} />,
                        checking:  <HourglassEmptyIcon     sx={{ fontSize: 16 }} />,
                    };
                    const statusText = {
                        available: 'Email i disponueshëm',
                        taken:     'Ky email është regjistruar tashmë',
                        invalid:   'Formati i emailit është i pavlefshëm',
                        same:      'Ky është emaili aktual i përdoruesit',
                        checking:  'Duke kontrolluar...',
                    };

                    return (
                        <>
                            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #3E8FA3 0%, #5AACBE 100%)' }} />
                            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Avatar sx={{ bgcolor: '#EEF2FF', width: 44, height: 44 }}>
                                    <AlternateEmailIcon sx={{ color: '#2563EB', fontSize: 22 }} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700}>Ndrysho emailin</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                                    </Typography>
                                </Box>
                            </Box>

                            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
                                {/* Current email display */}
                                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                        Email aktual
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <EmailIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                            {u.email}
                                        </Typography>
                                    </Stack>
                                </Box>

                                <Alert severity="warning" sx={{ mb: 2.5, fontSize: '0.8rem', py: 0.75 }}>
                                    Pas ndryshimit, përdoruesi duhet të hyjë me emailin e ri. Sigurohuni t'i njoftoni.
                                </Alert>

                                {/* New email */}
                                <Stack spacing={0.75} sx={{ mb: 2 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                                        Email i ri *
                                    </Typography>
                                    <TextField
                                        size="small" fullWidth
                                        placeholder="shembull@kompania.com"
                                        type="email"
                                        value={newEmail}
                                        onChange={e => {
                                            setEmailChangeData(p => ({ ...p, newEmail: e.target.value }));
                                            setEmailCheckResult(null);
                                        }}
                                        onBlur={e => checkEmailAvailability(e.target.value, u.id)}
                                        error={emailCheckResult === 'taken' || emailCheckResult === 'invalid' || emailCheckResult === 'same'}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AlternateEmailIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: emailCheckResult ? (
                                                <InputAdornment position="end">
                                                    <Stack direction="row" alignItems="center" spacing={0.5}
                                                        sx={{ color: statusColor[emailCheckResult] || 'text.secondary' }}>
                                                        {statusIcon[emailCheckResult]}
                                                        <Typography variant="caption" fontWeight={600} sx={{ color: 'inherit', whiteSpace: 'nowrap' }}>
                                                            {statusText[emailCheckResult]}
                                                        </Typography>
                                                    </Stack>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': emailCheckResult === 'available' ? {
                                                '& fieldset': { borderColor: '#16A34A' },
                                            } : {},
                                        }}
                                    />
                                </Stack>

                                {/* Confirm email */}
                                <Stack spacing={0.75}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                                        Konfirmo emailin e ri *
                                    </Typography>
                                    <TextField
                                        size="small" fullWidth
                                        placeholder="Rishkruaj emailin e ri"
                                        type="email"
                                        value={confirmEmail}
                                        onChange={e => setEmailChangeData(p => ({ ...p, confirmEmail: e.target.value }))}
                                        error={confirmEmail.length > 0 && !emailsMatch}
                                        helperText={
                                            confirmEmail.length > 0 && !emailsMatch
                                                ? 'Email-et nuk përputhen'
                                                : confirmEmail.length > 0 && emailsMatch
                                                    ? 'Email-et përputhen'
                                                    : ''
                                        }
                                        FormHelperTextProps={{
                                            sx: {
                                                color: confirmEmail.length > 0 && emailsMatch ? '#16A34A' : 'error.main',
                                                fontWeight: 600,
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SwapHorizIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: confirmEmail.length > 0 ? (
                                                <InputAdornment position="end">
                                                    {emailsMatch
                                                        ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                                                        : <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />}
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': confirmEmail.length > 0 ? {
                                                '& fieldset': { borderColor: emailsMatch ? '#16A34A' : undefined },
                                            } : {},
                                        }}
                                    />
                                </Stack>

                                {/* Ready indicator */}
                                {canSubmit && (
                                    <Box sx={{ mt: 2, p: 1.25, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 1.5 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16A34A' }} />
                                            <Typography variant="caption" fontWeight={600} sx={{ color: '#16A34A' }}>
                                                Gati për ndryshim: <strong>{u.email}</strong> → <strong>{newEmail.toLowerCase().trim()}</strong>
                                            </Typography>
                                        </Stack>
                                    </Box>
                                )}
                            </DialogContent>

                            <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                                <Button onClick={closeEmailChange} disabled={emailChangeSaving}
                                    variant="outlined" sx={{ borderRadius: 1.5, textTransform: 'none' }}>
                                    Anulo
                                </Button>
                                <Button
                                    onClick={saveEmailChange}
                                    disabled={!canSubmit}
                                    variant="contained"
                                    startIcon={emailChangeSaving
                                        ? <CircularProgress size={14} color="inherit" />
                                        : <AlternateEmailIcon />}
                                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, flex: 1 }}
                                >
                                    {emailChangeSaving ? 'Duke ndryshuar...' : 'Ndrysho emailin'}
                                </Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            {/* Redaktori i roleve u hoq nga këtu, jo u fshi: jeton te
                "Përdoruesit & Rolet" (UserManagementPage), i vetmi vend ku
                arrihet vërtet. Kjo kopje ishte e paarritshme —
                `openCustomRoleDialog` nuk thirrej askund, ndaj
                `customRoleDialog.open` mbetej `false` përgjithmonë. Çipat më
                sipër, që ZBATOJNË një rol te përdoruesi, mbeten. */}

            <Dialog open={!!manualInviteLink} onClose={() => setManualInviteLink('')} maxWidth="sm" fullWidth>
                <DialogTitle>Email-i i ftesës nuk u dërgua</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 1.5 }}>{manualInviteMessage}</Alert>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Kopjo linkun e fjalëkalimit dhe dërgoja përdoruesit në mënyrë të sigurt.
                    </Typography>
                    <TextField fullWidth size="small" label="Linku i ftesës" value={manualInviteLink}
                        InputProps={{ readOnly: true }} onFocus={e => e.target.select()} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManualInviteLink('')}>Mbyll</Button>
                    <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={async () => {
                        try { await navigator.clipboard.writeText(manualInviteLink); showSnack('Linku u kopjua.'); }
                        catch { showSnack('Kopjimi dështoi. Zgjidhe dhe kopjoje linkun nga fusha.', 'warning'); }
                    }}>Kopjo linkun</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackType} onClose={() => setSnack('')} sx={{ borderRadius:1.5 }}>
                    {snack}
                </Alert>
            </Snackbar>
        </Box>
        </Fragment>
    );
}
